/**
 * 「사흘」에서 **이야기가 걸려 있는 물건들.**
 *
 * `scenario/사흘/15_가구명세.md` 의 1순위다. 이 열 점은 단서 본문과 직접 붙어 있어서
 * 「무엇인지 한눈에 읽히는 것」이 품질 기준이다 — 플레이어가 연탄난로를 드럼통으로
 * 착각하면 작년 사건이 안 읽힌다.
 *
 * 그래서 여기 있는 것들은 **형태만으로 정체가 말해져야 한다.** 색이나 텍스처로 때우면
 * 어두운 실내에서 무너진다. 이 게임은 밤이고, 방 대부분은 스위치가 꺼져 있어서
 * 플레이어가 손전등으로 본다 (level-sahol.ts 의 init.on 을 보라). 손전등 원 안에서
 * 실루엣만 보고도 알아야 한다.
 *
 * 치수는 실물에서 가져왔다. 19공탄은 지름 150mm · 높이 142mm 이고, 연탄난로 몸통은
 * 그 연탄이 두 장 들어가는 통이다. 눈대중으로 넣으면 방에 놨을 때 바로 틀어진다.
 */

import * as THREE from 'three';
import { bevelBox, cappedArc, lathe, shellBox, type MeshWriter, type Slot } from './kernel';
import { rnd } from './saholEvidence';
import { paintGeometry, type Piece } from './pieces';
// 서랍 한 칸의 형태 — 잠금 캐비닛과 **같은 함수**를 쓴다. 복사하면 둘이 갈라진다
import { lockDrawerBuild } from './saholDevices';

/** 상자 하나 */
function box(
  w: MeshWriter,
  slot: Parameters<MeshWriter['add']>[1],
  sx: number,
  sy: number,
  sz: number,
  x: number,
  y: number,
  z: number,
  bevel = 0.003,
  uv = 0.7,
) {
  const g = bevelBox(sx, sy, sz, bevel);
  g.translate(x, y, z);
  w.add(g, slot, uv);
}

/** 세운 원통 하나 */
function tube(
  w: MeshWriter,
  slot: Parameters<MeshWriter['add']>[1],
  rTop: number,
  rBot: number,
  h: number,
  x: number,
  y: number,
  z: number,
  seg = 16,
  uv = 0.7,
  open = false,
) {
  const g = new THREE.CylinderGeometry(rTop, rBot, h, seg, 1, open);
  g.translate(x, y + h / 2, z);
  w.add(g, slot, uv);
}

export const SAHOL_STORY_PIECES: Piece[] = [
  /* ---------------------------------------------------------------- */
  /* 마을 창고 — 작년에 임세진이 죽은 곳                                */
  /* ---------------------------------------------------------------- */
  {
    id: 'briquette_stove',
    /* 블렌더로 다시 구워(tools/blender/stove.py) 문·재받이를 별도 노드로 냈다 (2026-09-16).
     * Tripo 단일 메시일 때는 문만 떼어낼 수 없어 유일하게 안 열리는 난로였다. 경첩은 문 왼쪽 모서리. */
    openable: [
      { part: '재받이 문', node: 'door', swing: 1.9, axis: 'y', pivot: [-0.088, 0.19, -0.163] },
      { part: '재받이', node: 'ashpan', slide: [0, 0, -0.2], inside: { w: 0.15, h: 0.08, d: 0.18, at: [0, 0.2, -0.1] } },
    ],
    glb: '/models/sahol/briquette_stove',
    name: '연탄난로',
    collection: 'sahol',
    category: '이야기',
    note:
      '작년 사건의 사인이다. 「반드시 형태가 읽혀야 한다」 — 드럼통으로 보이면 안 된다. ' +
      '연탄난로를 난로로 만드는 것은 몸통이 아니라 **연통과 아래 재받이 문**이다. ' +
      '몸통만 두면 통이고, 연통이 위로 뻗고 아래에 여닫는 문이 있으면 난로다.',
    dims: {
      /** 몸통 지름 — 19공탄(150mm)이 여유롭게 들어가는 통 */
      d: 0.36,
      /** 몸통 높이 — 연탄 두 장 + 재받이 */
      h: 0.62,
      /** 연통 지름 */
      flue: 0.104,
      /** 연통이 천장으로 뻗는 높이 */
      flueH: 1.5,
      /** 다리 높이 */
      leg: 0.11,
    },
    build(w, d) {
      const r = d.d / 2;
      const bodyY = d.leg;
      const bodyH = d.h - d.leg;

      // --- 몸통 ---
      // 아래가 살짝 넓다. 원통 그대로면 드럼통이 된다.
      w.part('몸통');
      tube(w, 'iron', r * 0.96, r, bodyH, 0, bodyY, 0, 20, 0.6);

      // --- 상판 ---
      // 몸통보다 넓게 내밀어야 「뚜껑을 얹은 통」이 된다. 가운데 구멍이 연탄 넣는 자리다.
      w.part('상판');
      tube(w, 'iron', r + 0.022, r + 0.022, 0.016, 0, d.h, 0, 20, 0.5);
      // 뚜껑 — 상판보다 조금 작고 살짝 올라앉는다
      tube(w, 'iron', r * 0.52, r * 0.56, 0.014, 0, d.h + 0.016, 0, 16, 0.4);
      // 뚜껑 손잡이 (고리를 걸어 여는 자리)
      box(w, 'iron', 0.052, 0.011, 0.014, 0, d.h + 0.036, 0, 0.002, 0.3);

      // --- 연통 ---
      // 이게 난로임을 말한다. 몸통 뒤쪽 위에서 꺾여 천장으로 간다.
      w.part('연통');
      const fr = d.flue / 2;
      // 몸통에서 뒤로 나오는 짧은 목
      const neck = new THREE.CylinderGeometry(fr, fr, 0.16, 12);
      neck.rotateX(Math.PI / 2);
      neck.translate(0, d.h - 0.1, -r - 0.05);
      w.add(neck, 'iron', 0.5);
      // 위로 꺾인 수직관
      tube(w, 'iron', fr, fr, d.flueH, 0, d.h - 0.1, -r - 0.13, 12, 0.6);
      // 이음 밴드 두 줄 — 관이 한 통이 아니라 이어 붙인 것으로 읽힌다
      for (const fy of [d.h + 0.32, d.h + 0.86]) {
        tube(w, 'iron', fr + 0.008, fr + 0.008, 0.022, 0, fy, -r - 0.13, 12, 0.3);
      }

      // --- 재받이 문 ---
      // 난로의 정체를 결정하는 두 번째 부품. 아래쪽에 여닫는 문이 있어야 한다.
      w.part('재받이 문');
      w.movable('재받이 문', () => {
      box(w, 'iron', d.d * 0.52, 0.15, 0.014, 0, bodyY + 0.11, -r + 0.004, 0.002, 0.4);
      // 문에 뚫린 공기 구멍 세 줄 (음각 대신 어두운 판을 얕게 박는다)
      for (let i = 0; i < 3; i++) {
        box(w, 'ebony', d.d * 0.34, 0.012, 0.006, 0, bodyY + 0.075 + i * 0.036, -r - 0.004, 0.001, 0.2);
      }
      // 문 손잡이
      box(w, 'iron', 0.018, 0.05, 0.02, d.d * 0.19, bodyY + 0.11, -r - 0.012, 0.002, 0.2);
      });
      w.part('재받이 속');   // 열면 회백색 재가 보인다
      box(w, 'plaster', d.d * 0.44, 0.05, 0.12, 0, bodyY + 0.055, -r + 0.08, 0.004, 0.3);

      // --- 다리 셋 ---
      w.part('다리');
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2 + Math.PI / 6;
        box(w, 'iron', 0.026, d.leg, 0.026, Math.sin(a) * r * 0.74, d.leg / 2, Math.cos(a) * r * 0.74, 0.002, 0.2);
      }
    },
  },

  {
    id: 'briquette',
    nofront: true,
    name: '연탄',
    collection: 'sahol',
    category: '이야기',
    note:
      '19공탄. 지름 150mm · 높이 142mm 가 실물이다. ' +
      '구멍을 실제로 뚫으면(CSG) 삼각형이 폭발하므로, 위아래 면에 어두운 원을 얕게 박아 ' +
      '구멍으로 읽히게 한다 — 손전등으로 볼 때 필요한 것은 「구멍 뚫린 검은 원통」이라는 인상이다.',
    dims: { d: 0.15, h: 0.142 },
    /* 연탄 하나를 통째로 집어 든다 (2026-09-16) */
    openable: [{ part: '연탄', slide: [0, 0, -0.06] }],
    materials() {
      return {
        // 연탄은 **검다.** stone(석재 회색)으로 두면 위에서 본 구멍만 검고 몸통이 밝아서
        // note 가 요구한 「구멍 뚫린 검은 원통」이라는 인상이 나오지 않는다 — 손전등으로
        // 비출 때 필요한 것이 바로 그 인상이다.
        coal: new THREE.MeshStandardMaterial({ color: '#2b2825', roughness: 0.96, metalness: 0 }),
      };
    },
    vary: (seed, d) => { const r = (k: number) => { const v = Math.sin(seed * 12.9898 + k * 78.233) * 43758.5453; return v - Math.floor(v); }; return { ...d, h: d.h * (0.94 + r(1) * 0.12), d: d.d * (0.97 + r(2) * 0.06) }; },
    build(w, d) {
      const r = d.d / 2;
      w.movable('연탄', () => {
      w.part('탄');
      tube(w, 'coal', r, r * 0.985, d.h, 0, 0, 0, 18, 0.35);

      // 19공: 가운데 1 + 안쪽 6 + 바깥 12
      w.part('구멍');
      const holes: [number, number][] = [[0, 0]];
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        holes.push([Math.cos(a) * r * 0.38, Math.sin(a) * r * 0.38]);
      }
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2 + Math.PI / 12;
        holes.push([Math.cos(a) * r * 0.68, Math.sin(a) * r * 0.68]);
      }
      for (const [hx, hz] of holes) {
        // 위아래 양쪽에 박는다. 쌓아 놓으면 아래 면도 보인다
        for (const [hy, dep] of [
          [d.h - 0.006, 0.012],
          [-0.006, 0.012],
        ] as const) {
          tube(w, 'ebony', 0.0115, 0.0115, dep, hx, hy, hz, 8, 0.2);
        }
      }
      });
    },
  },

  {
    id: 'incinerator',
    name: '소각로',
    collection: 'sahol',
    category: '이야기',
    note:
      '배정호가 12/18 낮에 종이를 태운 곳. 자개 단추 두 개가 재 속에 남았다. ' +
      '드럼통을 세워 쓰는 시골 소각로다 — 위가 열려 있고 안쪽이 그을려 있어야 한다.',
    dims: {
      d: 0.58,
      h: 0.86,
      /** 벽 두께 (열린 통이라 두께가 보인다) */
      t: 0.014,
    },
    /** 드럼통 뚜껑을 옆으로 밀어 연다 — 재 속의 자개 단추를 찾는 동작 (2026-09-16 상호작용) */
    openable: [
      { part: '뚜껑', slide: [0.42, 0, 0], inside: { w: 0.5, h: 0.8, d: 0.5, at: [0, 0.45, 0] } },
    ],
    build(with_, d) {
      const w = with_;
      const r = d.d / 2;

      // --- 통 ---
      // 위가 열린 통이다. **열린 실린더로 만들면 안 된다** — 뒷면 컬링 때문에 앞면이
      // 투과돼 속이 들여다보인다 (tools/solid-check.mjs 가 이걸 잡는다).
      // 벽 단면을 폐곡선으로 그려 lathe 로 돌리면 두께 있는 통이 **닫힌 몸체**로 나온다.
      w.part('통');
      w.add(
        lathe(
          [
            [r, 0],
            [r, d.h],
            [r - d.t, d.h],
            [r - d.t, 0],
            [r, 0],
          ],
          20,
        ),
        'iron',
        0.6,
      );
      // 안쪽 그을음 — 벽 안쪽에 살짝 작은 통을 하나 더 넣는다. 이것도 닫힌 몸체다.
      w.part('그을음');
      w.add(
        lathe(
          [
            [r - d.t - 0.001, 0.05],
            [r - d.t - 0.001, d.h - 0.02],
            [r - d.t - 0.008, d.h - 0.02],
            [r - d.t - 0.008, 0.05],
            [r - d.t - 0.001, 0.05],
          ],
          20,
        ),
        'ebony',
        0.5,
      );

      // --- 바닥 (재가 깔린다) ---
      w.part('재');
      tube(w, 'ebony', r - d.t, r - d.t, 0.05, 0, 0.02, 0, 20, 0.3);

      // --- 몸통 리브 두 줄 ---
      // 드럼통을 드럼통으로 만드는 것이 이 두 줄이다
      w.part('리브');
      for (const ry of [d.h * 0.3, d.h * 0.66]) {
        tube(w, 'iron', r + 0.012, r + 0.012, 0.028, 0, ry, 0, 20, 0.3);
      }

      // --- 공기 구멍 (아래쪽) ---
      w.part('공기 구멍');
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2;
        box(
          w,
          'ebony',
          0.07,
          0.05,
          0.02,
          Math.sin(a) * r,
          0.13,
          Math.cos(a) * r,
          0.002,
          0.2,
        );
      }

      // --- 얹힌 뚜껑 — 얇은 원판과 가운데 손잡이. 옆으로 밀면 재가 드러난다
      w.movable('뚜껑', () => {
      w.part('뚜껑');
      const cap = new THREE.CylinderGeometry(r + 0.008, r + 0.008, 0.012, 22);
      cap.translate(0, d.h + 0.006, 0);
      w.add(cap, 'iron', 0.45);
      w.part('뚜껑 손잡이');
      const grip = new THREE.CylinderGeometry(0.012, 0.012, 0.14, 10);
      grip.rotateZ(Math.PI / 2);
      grip.translate(0, d.h + 0.024, 0);
      w.add(grip, 'iron', 0.2);
      });
    },
  },

  /* ---------------------------------------------------------------- */
  /* 공용 거실과 주방 — 사흘 트릭의 물건이 있는 곳                       */
  /* ---------------------------------------------------------------- */
  {
    id: 'sink_unit',
    name: '싱크대',
    collection: 'sahol',
    category: '이야기',
    note:
      '**빈 그릇 세 벌이 올라간 곳.** 핵심 단서다. 그릇이 올라갈 자리(상판 오른쪽)가 ' +
      '평평하게 비어 있어야 하고, 볼은 왼쪽에 있어야 한다. ' +
      '스테인리스 상판과 물 빠짐 홈이 있어야 싱크대로 읽힌다.',
    dims: {
      w: 1.24,
      d: 0.6,
      h: 0.86,
      /** 상판 두께 */
      top: 0.04,
      /** 볼 깊이 */
      bowl: 0.17,
    },
    openable: [
      { part: '왼짝', swing: 1.5, axis: 'y', pivot: [-0.605, 0.4, -0.294], inside: { w: 0.5, h: 0.5, d: 0.45, at: [-0.3, 0.33, 0] } },
      { part: '오른짝', swing: -1.5, axis: 'y', pivot: [0.605, 0.4, -0.294], inside: { w: 0.5, h: 0.5, d: 0.45, at: [0.3, 0.33, 0] } },
    ],
    build(w, d) {
      const hw = d.w / 2;
      const hd = d.d / 2;

      // --- 하부장 ---
      w.part('하부장');
      // 앞이 뚫린 통 — 문을 열면 배수 트랩·양동이·세제가 보여야 한다(2026-09-15). 통짜면 판때기다
      const cabH = d.h - d.top - 0.06;
      for (const g of shellBox(d.w, cabH, d.d - 0.03, 0.016, ['-z'])) { g.translate(0, 0.06, 0); w.add(g, 'paint', 1.0); }
      w.part('하부장 속');
      const trap1 = new THREE.CylinderGeometry(0.02, 0.02, 0.2, 10); trap1.translate(0, 0.06 + cabH - 0.1, 0.05); w.add(trap1, 'steel', 0.2);
      const trap2 = new THREE.CylinderGeometry(0.02, 0.02, 0.18, 10); trap2.rotateX(Math.PI / 2); trap2.translate(0, 0.06 + cabH - 0.2, 0.14); w.add(trap2, 'steel', 0.2);
      const trap3 = new THREE.CylinderGeometry(0.02, 0.02, 0.32, 10); trap3.translate(0, 0.06 + cabH - 0.36, 0.23); w.add(trap3, 'steel', 0.2);
      const pail = lathe([[0.11, 0], [0.13, 0.02], [0.14, 0.24], [0.13, 0.25], [0.12, 0.25], [0.12, 0.02]], 18, false); pail.translate(-0.3, 0.076, 0.04); w.add(pail, 'plastic', 0.4);
      for (const [x, h, sl] of [[0.28, 0.24, 'painted'], [0.4, 0.2, 'plastic']] as const) {
        const b = lathe([[0.035, 0], [0.038, 0.02], [0.036, h - 0.06], [0.018, h - 0.03], [0.018, h], [0, h]], 12); b.translate(x, 0.076, 0.06); w.add(b, sl, 0.4);
      }
      const rag = bevelBox(0.2, 0.03, 0.14, 0.01, 2); rag.translate(0.05, 0.076 + 0.015, 0.02); w.add(rag, 'fabric', 0.5);
      // 걸레받이 — 하부장이 바닥에서 조금 떠야 「짜 넣은 가구」로 보인다
      box(w, 'ebony', d.w - 0.06, 0.06, d.d - 0.09, 0, 0.03, 0, 0.002, 0.4);

      // --- 문 두 짝 ---
      w.part('문');
      const doorH = d.h - d.top - 0.1;
      for (const side of [-1, 1]) {
        w.movable(side < 0 ? '왼짝' : '오른짝', () => {
          box(w, 'paint', hw - 0.03, doorH, 0.018, side * (hw / 2), 0.08 + doorH / 2, -hd + 0.006, 0.003, 0.9);
          // 손잡이 — 세로 바
          box(w, 'alu', 0.016, 0.11, 0.022, side * 0.06, 0.08 + doorH / 2, -hd - 0.008, 0.002, 0.2);
        });
      }

      // --- 스테인리스 상판 ---
      w.part('상판');
      const topY = d.h - d.top;
      const bx = -d.w * 0.26;
      const bw = 0.42;
      const bd = 0.36;
      // **볼 구멍을 둘러 짠다.** 통판 하나로 덮으면 볼이 그 아래 완전히 숨어 개수대가
      // 보이지 않는다 — 위에서 봐도 홈만 그어진 평평한 작업대로 보였다(여섯 방향 시트).
      // 「상판을 둘러 얹는다」는 주석의 의도를 실제로 구현한다.
      const capY = topY + d.top / 2;
      const leftW = bx - bw / 2 + d.w / 2;
      box(w, 'steel', leftW, d.top, d.d, -d.w / 2 + leftW / 2, capY, 0, 0.003, 1.2);
      const rightW = d.w / 2 - (bx + bw / 2);
      box(w, 'steel', rightW, d.top, d.d, d.w / 2 - rightW / 2, capY, 0, 0.003, 1.2);
      const sideD = (d.d - bd) / 2;
      for (const sz of [-1, 1]) {
        box(w, 'steel', bw, d.top, sideD, bx, capY, sz * (bd / 2 + sideD / 2), 0.003, 1.2);
      }
      // 뒤쪽 물막이 — 벽으로 물이 넘어가지 않게 세운 턱
      box(w, 'steel', d.w, 0.07, 0.02, 0, d.h + 0.035, hd - 0.01, 0.002, 0.5);

      // --- 볼 (왼쪽) ---
      // 상판을 파낼 수 없으므로 벽 넷과 바닥으로 오목한 통을 만들고 상판을 둘러 얹는다.
      w.part('볼');
      const bt = 0.012;
      for (const [sx, sz, sw, sd] of [
        [bx - bw / 2 + bt / 2, 0, bt, bd],
        [bx + bw / 2 - bt / 2, 0, bt, bd],
        [bx, -bd / 2 + bt / 2, bw, bt],
        [bx, bd / 2 - bt / 2, bw, bt],
      ] as const) {
        box(w, 'steel', sw, d.bowl, sd, sx, topY - d.bowl / 2, sz, 0.001, 0.5);
      }
      box(w, 'steel', bw, bt, bd, bx, topY - d.bowl, 0, 0.001, 0.5);
      // 배수구
      tube(w, 'alu', 0.032, 0.032, 0.008, bx, topY - d.bowl + bt, 0, 12, 0.2);

      // --- 물 빠짐 홈 (오른쪽 — 그릇을 놓는 자리) ---
      // 얕은 홈 다섯 줄. 이게 있으면 오른쪽이 「말리는 자리」로 읽힌다.
      w.part('물 빠짐 홈');
      for (let i = 0; i < 5; i++) {
        box(
          w,
          'alu',
          0.006,
          0.004,
          d.d * 0.52,
          d.w * 0.12 + i * 0.072,
          topY + d.top - 0.001,
          0.02,
          0.0005,
          0.2,
        );
      }

      // --- 수전 ---
      w.part('수전');
      const fx = bx;
      const fz = hd - 0.08;
      tube(w, 'alu', 0.026, 0.03, 0.05, fx, d.h, fz, 12, 0.2);
      tube(w, 'alu', 0.017, 0.017, 0.22, fx, d.h + 0.05, fz, 12, 0.3);
      // 굽은 목 — 앞으로 뻗어 볼 위에 온다
      const spout = new THREE.CylinderGeometry(0.014, 0.014, 0.2, 10);
      spout.rotateX(Math.PI / 2);
      spout.translate(fx, d.h + 0.26, fz - 0.1);
      w.add(spout, 'alu', 0.3);
      // 레버
      box(w, 'alu', 0.09, 0.016, 0.02, fx + 0.03, d.h + 0.28, fz + 0.01, 0.002, 0.2);
    },
  },

  {
    id: 'tray_set',
    mount: 'surface',
    nofront: true,
    name: '쟁반 세 벌',
    collection: 'sahol',
    category: '이야기',
    note:
      '**사흘 트릭의 물건이다.** 밥을 세 번 가져다 준 흔적인데 그릇이 비어 있다. ' +
      '「형태가 읽혀야 한다」 — 쟁반 위에 밥그릇·국그릇·접시가 각각 놓여 있어야 ' +
      '한 끼 상차림 세 벌로 보인다. 쟁반만 겹쳐 두면 그냥 접시 더미다.',
    /** 쟁반 세 벌을 어긋나게 겹쳐 놓는다 */
    clusters: 3,
    dims: {
      /** 쟁반 하나 */
      w: 0.34,
      d: 0.24,
      /** 쟁반 테두리 높이 */
      rim: 0.018,
      /** 세 벌을 어긋나게 놓는 간격 */
      step: 0.026,
    },
    /* 맨 위 한 상을 통째로 들어 올린다 — 밥그릇·국그릇·접시·숟가락째 (2026-09-16) */
    openable: [{ part: '쟁반 한 벌', slide: [0, 0, -0.1] }],
    build(w, d) {
      for (let i = 0; i < 3; i++) {
        const y = i * d.step;
        // 완전히 겹쳐 쌓으면 한 장으로 보인다. 조금씩 어긋나게 놓는다
        // **그릇이 보일 만큼** 어긋나야 한다. 2.2cm 로는 아래 쟁반이 테두리만 드러나서
        // note 가 요구한 「상차림 세 벌」이 맨 위 한 벌로 줄어든다(여섯 방향 시트에서
        // 아래 둘이 빈 쟁반으로 보였다). 11cm 어긋나면 각 쟁반에 그릇을 올릴 자리가 난다.
        const ox = (i - 1) * 0.11;
        const oz = (i % 2 === 0 ? 1 : -1) * 0.02;

        const draw = () => {
        w.part(`쟁반 ${i + 1}`);
        box(w, 'alu', d.w, 0.006, d.d, ox, y + 0.003, oz, 0.001, 0.4);
        // 테두리 네 변 — 이게 없으면 판때기다
        for (const [sx, sz, sw, sd] of [
          [ox - d.w / 2 + 0.005, oz, 0.01, d.d],
          [ox + d.w / 2 - 0.005, oz, 0.01, d.d],
          [ox, oz - d.d / 2 + 0.005, d.w, 0.01],
          [ox, oz + d.d / 2 - 0.005, d.w, 0.01],
        ] as const) {
          box(w, 'alu', sw, d.rim, sd, sx, y + 0.006 + d.rim / 2, sz, 0.001, 0.25);
        }

        // 그릇 — **쟁반마다** 올린다. 아래 두 벌은 위 쟁반에 덮이지 않는 쪽(x 가 작은
        // 쪽) 끝에 밥그릇 하나만 놓아 「한 끼가 있었다」를 보이고, 맨 위는 한 상 전부.
        const gx = i === 2 ? ox : ox - 0.105;
        w.part('밥그릇');
        tube(w, 'porcelain', 0.058, 0.042, 0.052, i === 2 ? gx - 0.07 : gx, y + 0.009, oz - 0.03, 14, 0.2);
        if (i === 2) {
          w.part('국그릇');
          tube(w, 'porcelain', 0.066, 0.048, 0.038, ox + 0.05, y + 0.009, oz - 0.032, 14, 0.2);
          w.part('접시');
          tube(w, 'porcelain', 0.052, 0.046, 0.012, ox + 0.005, y + 0.009, oz + 0.055, 14, 0.2);
          w.part('숟가락');
          box(w, 'steel', 0.014, 0.004, 0.11, ox - 0.008, y + 0.011, oz + 0.008, 0.001, 0.15);
        }
        };
        // 맨 위 쟁반만 여닫이 — 한 상째 들어 올린다
        if (i === 2) w.movable('쟁반 한 벌', draw); else draw();
      }
    },
  },

  /* ---------------------------------------------------------------- */
  /* 목공방 — 알리바이가 걸린 방                                        */
  /* ---------------------------------------------------------------- */
  {
    id: 'dust_collector',
    name: '집진기',
    collection: 'sahol',
    category: '이야기',
    note:
      '**알리바이의 핵심 물건이다.** 22:04 에 켜지고 00:58 까지 돈다 — 사람이 없어도 돈다. ' +
      '그래서 「돌고 있다」가 형태로 보여야 한다: 부푼 천 자루가 위에 달려 있어야 한다. ' +
      '자루가 축 처져 있으면 꺼진 것으로 읽힌다.',
    dims: {
      /** 하부 몸통 지름 */
      d: 0.46,
      /** 몸통 높이 */
      h: 0.72,
      /** 자루 지름 */
      bag: 0.5,
      /** 자루 높이 */
      bagH: 0.92,
    },
    /** 천 자루를 조임 밴드에서 들어 올려 벗긴다 — 자루 속 먼지를 뒤지는 동작 (2026-09-16 상호작용) */
    openable: [
      { part: '자루', slide: [0, 0.55, 0], inside: { w: 0.4, h: 0.85, d: 0.4, at: [0, 1.1, 0] } },
    ],
    build(w, d) {
      const r = d.d / 2;
      const br = d.bag / 2;

      // --- 바퀴 달린 받침 ---
      w.part('받침');
      box(w, 'steel', d.d * 0.9, 0.03, d.d * 0.9, 0, 0.075, 0, 0.003, 0.4);
      w.part('바퀴');
      for (const [wx, wz] of [
        [-r * 0.62, -r * 0.62],
        [r * 0.62, -r * 0.62],
        [-r * 0.62, r * 0.62],
        [r * 0.62, r * 0.62],
      ] as const) {
        const wheel = new THREE.CylinderGeometry(0.03, 0.03, 0.022, 10);
        wheel.rotateZ(Math.PI / 2);
        wheel.translate(wx, 0.03, wz);
        w.add(wheel, 'rubber', 0.2);
      }

      // --- 모터 몸통 ---
      w.part('모터');
      tube(w, 'steel', r * 0.86, r * 0.9, d.h * 0.52, 0, 0.09, 0, 18, 0.6);
      // 모터 냉각 리브
      for (let i = 0; i < 10; i++) {
        const a = (i / 10) * Math.PI * 2;
        box(
          w,
          'steel',
          0.012,
          d.h * 0.34,
          0.026,
          Math.sin(a) * r * 0.9,
          0.09 + d.h * 0.26,
          Math.cos(a) * r * 0.9,
          0.001,
          0.2,
        );
      }

      // --- 임펠러 하우징 (나선형 몸체) ---
      w.part('하우징');
      tube(w, 'steel', r, r, 0.2, 0, 0.09 + d.h * 0.52, 0, 20, 0.5);

      // --- 흡입구 (호스가 붙는 곳) ---
      w.part('흡입구');
      const inlet = new THREE.CylinderGeometry(0.05, 0.05, 0.14, 12);
      inlet.rotateX(Math.PI / 2);
      inlet.translate(0, 0.09 + d.h * 0.52 + 0.1, -r - 0.05);
      w.add(inlet, 'steel', 0.3);

      // --- 주름 호스 ---
      // 마디를 실제로 만들어야 호스로 읽힌다. 매끈한 관은 파이프다.
      // 호스 — 마디 사이가 벌어지면 마디마다 별개 덩이가 된다 (70~223mm 로 잡혔다).
      // 마디 수를 늘리고 높이를 키워 서로 물리게 한다. 주름은 마디가 겹쳐야 주름이다.
      w.part('호스');
      const hoseN = 16;
      for (let i = 0; i < hoseN; i++) {
        const t = i / (hoseN - 1);
        const hz = -r - 0.12 - t * 0.34;
        const hy = 0.09 + d.h * 0.52 + 0.1 - t * t * 0.32;
        tube(w, 'rubber', 0.048, 0.048, 0.05, 0, hy, hz, 10, 0.2);
      }

      // --- 천 자루 (부풀어 있다 = 돌고 있다) ---
      w.part('자루');
      const ringY = 0.09 + d.h * 0.52 + 0.2;
      // 아래 조임 밴드 — 자루를 물고 있는 쪽이라 기계에 남는다
      tube(w, 'steel', br * 0.92, br * 0.92, 0.026, 0, ringY, 0, 18, 0.2);
      w.movable('자루', () => {
      // 자루 몸통 — 가운데가 불룩하게 3단으로
      tube(w, 'fabric', br, br * 0.9, d.bagH * 0.3, 0, ringY + 0.026, 0, 18, 0.8);
      tube(w, 'fabric', br * 0.97, br, d.bagH * 0.4, 0, ringY + 0.026 + d.bagH * 0.3, 0, 18, 0.8);
      tube(
        w,
        'fabric',
        br * 0.72,
        br * 0.97,
        d.bagH * 0.3,
        0,
        ringY + 0.026 + d.bagH * 0.7,
        0,
        18,
        0.8,
      );
      // 위 매듭
      w.part('매듭');
      tube(w, 'fabric', br * 0.3, br * 0.72, 0.07, 0, ringY + 0.026 + d.bagH, 0, 14, 0.4);
      });
      // 걸어 매는 고리
      box(w, 'steel', 0.02, 0.06, 0.02, 0, ringY + 0.096 + d.bagH, 0, 0.002, 0.2);
    },
  },

  /* ---------------------------------------------------------------- */
  /* 로스터리 — 차유리의 작업장                                         */
  /* ---------------------------------------------------------------- */
  {
    id: 'drum_roaster',
    name: '드럼 로스터',
    collection: 'sahol',
    category: '이야기',
    note:
      '이 방의 주인공이다. 23:20 에 로그가 끊기고 00:10 에 다시 켜진다. ' +
      '로스터를 로스터로 만드는 것은 **눕힌 드럼 + 위의 호퍼 + 앞의 냉각 트레이** 셋이다. ' +
      '드럼만 있으면 세탁기로 보인다.',
    /* **크기가 1kg 샘플 로스터였다** (2026-09-11, Chris: 「여기가 로스팅하는 곳임?」).
     * 폭 56cm 짜리가 방 구석에 서 있으니 컷에서 로스터가 어디 있는지도 안 보였다 —
     * note 는 「이 방의 주인공」이라고 써 놓고 크기가 주인공이 못 됐다.
     * 가게 로스터(5kg 급) 실측으로 올린다: 폭 1.05 · 드럼 지름 0.42 · 드럼 중심 1.02 ·
     * 냉각 트레이 0.72. 안쪽 부품 위치는 전부 이 값에서 유도되므로 같이 커진다. */
    dims: {
      /** 드럼 지름 */
      drum: 0.42,
      /** 드럼 길이 (앞뒤) */
      len: 0.55,
      /** 드럼 중심 높이 */
      drumY: 1.02,
      /** 냉각 트레이 지름 */
      tray: 0.72,
      /** 프레임 폭 */
      w: 1.05,
    },
    /** 앞 도어가 왼쪽 변을 축으로 열린다 — 드럼 안을 들여다보는 동작 (2026-09-16 상호작용) */
    openable: [
      { part: '도어', swing: 1.9, axis: 'y', pivot: [-0.197, 1.02, -0.29], inside: { w: 0.38, h: 0.38, d: 0.5, at: [0, 1.02, 0] } },
    ],
    build(w, d) {
      const dr = d.drum / 2;
      const hw = d.w / 2;

      // --- 프레임 ---
      w.part('프레임');
      for (const sx of [-hw + 0.03, hw - 0.03]) {
        for (const sz of [-0.2, 0.2]) {
          box(w, 'steel', 0.04, d.drumY - 0.1, 0.04, sx, (d.drumY - 0.1) / 2, sz, 0.003, 0.3);
        }
      }
      // 아래 묶음대
      box(w, 'steel', d.w, 0.035, 0.42, 0, 0.06, 0, 0.003, 0.4);
      // **드럼을 받치는 상단 가로대 둘.** 이게 없으면 다리 x(±0.25)와 드럼 x(±0.16)가
      // 9cm 벌어져서 드럼이 다리 사이 공중에 뜬다 — joint-check 가 프레임을 몸통에서
      // 70mm 떨어진 별개 덩이로 잡았다. 실제 로스터도 드럼축을 받는 가로대가 있다.
      box(w, 'steel', d.w - 0.06, 0.04, 0.05, 0, d.drumY - dr - 0.02, -0.2, 0.003, 0.3);
      box(w, 'steel', d.w - 0.06, 0.04, 0.05, 0, d.drumY - dr - 0.02, 0.2, 0.003, 0.3);
      // 드럼축 베어링 블록 — 가로대와 드럼을 실제로 물린다
      for (const sz of [-0.2, 0.2]) {
        box(w, 'steel', 0.09, 0.07, 0.06, 0, d.drumY - dr + 0.015, sz, 0.003, 0.25);
      }

      // --- 드럼 (눕혀 있다) ---
      w.part('드럼');
      const drumG = new THREE.CylinderGeometry(dr, dr, d.len, 20);
      drumG.rotateX(Math.PI / 2);
      drumG.translate(0, d.drumY, 0);
      w.add(drumG, 'steel', 0.7);
      // 드럼 밴드 두 줄
      for (const bz of [-d.len * 0.28, d.len * 0.28]) {
        const band = new THREE.CylinderGeometry(dr + 0.01, dr + 0.01, 0.03, 20);
        band.rotateX(Math.PI / 2);
        band.translate(0, d.drumY, bz);
        w.add(band, 'iron', 0.3);
      }

      // --- 앞 도어 (유리창 + 손잡이) ---
      // 여기로 볶이는 콩을 본다. 이 창이 로스터의 얼굴이다.
      w.movable('도어', () => {
      w.part('도어');
      const doorG = new THREE.CylinderGeometry(dr * 0.94, dr * 0.94, 0.03, 20);
      doorG.rotateX(Math.PI / 2);
      doorG.translate(0, d.drumY, -d.len / 2 - 0.015);
      w.add(doorG, 'iron', 0.4);
      w.part('도어 유리');
      const glass = new THREE.CylinderGeometry(dr * 0.52, dr * 0.52, 0.008, 16);
      glass.rotateX(Math.PI / 2);
      glass.translate(0, d.drumY, -d.len / 2 - 0.032);
      w.add(glass, 'pane', 0.3);
      w.part('도어 손잡이');
      box(w, 'ebony', 0.028, 0.13, 0.03, dr * 0.72, d.drumY, -d.len / 2 - 0.04, 0.003, 0.2);
      });

      // --- 호퍼 (위에서 생두를 넣는 깔때기) ---
      // 위아래가 뚫린 깔때기다. 열린 원뿔로 만들면 투과된다 — 두께 있는 폐단면으로 돌린다.
      w.part('호퍼');
      const hop = lathe(
        [
          [0.15, 0.18],
          [0.055, 0],
          [0.048, 0],
          [0.143, 0.18],
          [0.15, 0.18],
        ],
        16,
      );
      hop.translate(0, d.drumY + dr + 0.02, 0.02);
      w.add(hop, 'steel', 0.6);
      // 호퍼 목
      tube(w, 'steel', 0.05, 0.05, 0.04, 0, d.drumY + dr - 0.02, 0.02, 12, 0.3);

      // --- 냉각 트레이 (앞 아래, 볶은 콩을 식히는 원반) ---
      w.part('냉각 트레이');
      const tr = d.tray / 2;
      // 얕은 통 — 소각로와 같은 이유로 폐단면 lathe 로 만든다
      const trayWall = lathe(
        [
          [tr, 0],
          [tr, 0.05],
          [tr - 0.012, 0.05],
          [tr - 0.012, 0],
          [tr, 0],
        ],
        20,
      );
      trayWall.translate(0, d.drumY - 0.42, -0.3);
      w.add(trayWall, 'steel', 0.6);
      const trayFloor = new THREE.CylinderGeometry(tr - 0.012, tr - 0.012, 0.008, 20);
      trayFloor.translate(0, d.drumY - 0.42 + 0.004, -0.3);
      w.add(trayFloor, 'alu', 0.4);
      // **트레이를 프레임에 매는 팔 둘.** 트레이 x(±0.22)가 다리 x(0.23~0.27)에 1cm 모자라
      // 공중에 떠 있었다(184mm 떨어진 덩이로 잡혔다). 실물도 트레이는 몸체에 매달린다.
      w.part('트레이 팔');
      for (const sx of [-1, 1]) {
        box(w, 'steel', 0.16, 0.03, 0.035, sx * (hw - 0.09), d.drumY - 0.42 + 0.02, -0.2, 0.003, 0.25);
      }
      // 저어 주는 팔 두 개
      w.part('교반 팔');
      for (const a of [0, Math.PI]) {
        box(
          w,
          'steel',
          tr * 0.86,
          0.016,
          0.02,
          Math.cos(a) * tr * 0.42,
          d.drumY - 0.39,
          -0.3 + Math.sin(a) * tr * 0.42,
          0.002,
          0.2,
        );
      }
      tube(w, 'steel', 0.02, 0.02, 0.09, 0, d.drumY - 0.42, -0.3, 10, 0.2);

      // --- 계기판 ---
      // 온도계와 스위치. 「로그가 남는 기계」라는 게 여기서 읽힌다.
      w.part('계기판');
      box(w, 'steel', 0.2, 0.15, 0.04, hw + 0.06, d.drumY + 0.02, -0.1, 0.003, 0.4);
      // **프레임 다리까지 내려오는 브래킷.** 계기판 y(0.805~0.955)가 다리 상단(0.76)보다
      // 위에 있어서 붙을 데가 없었다 — 70mm 떨어진 덩이로 잡혔다.
      // z 로 길게 뻗는다: 계기판은 z -0.1, 다리는 z ±0.2 라서 얇은 기둥으로는 둘 중
      // 하나에만 닿는다(58mm 남았다). z -0.22~-0.08 이면 양쪽 다 물린다.
      box(w, 'steel', 0.035, 0.22, 0.14, hw - 0.02, d.drumY - 0.09, -0.15, 0.002, 0.25);
      w.part('온도계');
      const dial = new THREE.CylinderGeometry(0.052, 0.052, 0.012, 16);
      dial.rotateX(Math.PI / 2);
      dial.translate(hw + 0.06, d.drumY + 0.05, -0.122);
      w.add(dial, 'brass', 0.2);
      const face = new THREE.CylinderGeometry(0.044, 0.044, 0.004, 16);
      face.rotateX(Math.PI / 2);
      face.translate(hw + 0.06, d.drumY + 0.05, -0.13);
      w.add(face, 'alu', 0.15);  // 문자판 — 슬롯 예산 때문에 paper 대신 alu(은색 판)
      w.part('스위치');
      for (let i = 0; i < 3; i++) {
        box(w, 'ebony', 0.022, 0.03, 0.016,  // 검정 스위치 — 슬롯 예산 때문에 plastic 대신 ebony
           hw + 0.02 + i * 0.032, d.drumY - 0.03, -0.126, 0.002, 0.1);
      }

      // --- 배기관 ---
      w.part('배기관');
      /* **연통이 로스터리의 실루엣을 만든다** — 참고 사진(Ipsento)에서 굵은 관이 천장까지
       * 올라간다. 5.2cm 짜리가 2.1m 에서 끊겨 있어 난로 연통으로 보였다. 13cm 로 굵히고
       * 천장(2.6m) 바로 아래까지 뽑는다. 천장이 더 낮은 방에 놓으면 길이를 줄여야 한다. */
      tube(w, 'iron', 0.13, 0.13, 1.15, 0, d.drumY + dr + 0.2, 0.2, 14, 0.6);
    },
  },

  /* ---------------------------------------------------------------- */
  /* 한서진의 집 — 현장                                                */
  /* ---------------------------------------------------------------- */
  {
    id: 'builtin_closet',
    name: '붙박이장',
    collection: 'sahol',
    category: '이야기',
    note: '벽에 짜 넣은 붙박이장. 여닫이 두 짝, 안쪽에 선반 한 장과 옷걸이 봉. `clothes` 를 1 로 두면 옷이 걸리고 0 이면 빈 장이다.',
    /* 설계 메모 (카탈로그 설명에 넣지 말 것 — 사건 얘기는 조각의 설명이 아니다, 2026-09-15 Chris):
     * 「문이 열리고 안이 보여야 한다」는 상태는 배치에서 문짝 프롭에 `init: { open: true }` 를 줘서 만든다.
     * 조각은 닫힌 채 구워진다 — 각도를 구워 두면 프롭이 여는 만큼이 더해져 문이 몸통을 뚫는다. */
    dims: {
      w: 1.6,
      h: 2.2,
      d: 0.62,
      /** 몸통 판 두께 */
      t: 0.026,
      /**
       * 왼짝을 **구워 두는** 각도 (라디안). **기본은 0(닫힘)이다** — 이제 짝이 `openable` 로
       * 움직이므로, 여기에 각도를 구워 두면 프롭이 여는 만큼이 그 위에 **더해져** 문이
       * 몸통을 뚫고 돌아간다(2026-09-12 열림 컷에서 2.35rad 까지 갔다). 열린 채로 두고
       * 싶으면 배치에서 문짝 프롭에 `init: { open: true }` 를 준다.
       */
      ajar: 0,
      /** 오른짝을 구워 두는 각도. 위와 같은 이유로 기본 0 */
      ajarR: 0,
      /** 선반 높이 */
      shelfY: 1.72,
      /** 옷 — 0 빈 장(시신이 든 장처럼 비워야 할 때), 1 걸린 옷·개어 둔 옷 */
      clothes: 1,
    },
    /* **두 짝을 E 로 여닫는다** (2026-09-12). 세로 경첩이므로 y 축이다 — x·z 로 돌리면 문이
     * 옆으로 눕는다. 경첩은 몸통 좌·우 안쪽 기둥(±(w/2 − t))이고, `dims.ajar` 는 그대로 **구워
     * 두는 각도**로 남는다(쓰레기통과 같은 규약) — 0 이면 닫힌 채 지어지고 프롭이 연다.
     * 축 좌표는 정적이라 기본 dims(w 1.6 · t 0.026 · h 2.2 · d 0.62)에서 나온 수를 적는다:
     * x = ±(0.8 − 0.026) = ±0.774 · y = h/2 = 1.1 · z = −d/2 + leafT/2 = −0.299
     * 담는 곳은 선반 위 — 옷장 속에 물건을 넣을 수 있다 */
    openable: [
      { part: '왼짝', swing: 1.5, axis: 'y', pivot: [-0.774, 1.1, -0.299],
        inside: { w: 1.4, h: 0.4, d: 0.5, at: [0, 1.78, 0] } },
      { part: '오른짝', swing: -1.5, axis: 'y', pivot: [0.774, 1.1, -0.299] },
    ],
    build(w, d) {
      const hw = d.w / 2;
      const t = d.t;
      const inner = d.d - t;

      // --- 몸통 (벽에 짜 넣은 통) ---
      // 뒤판을 두고 좌우·위아래를 판으로 짠다. 안쪽이 보이므로 판 두께가 실제로 읽힌다.
      w.part('몸통');
      box(w, 'trim', d.w, t, d.d, 0, t / 2, 0, 0.002, 1.2);                    // 밑판
      box(w, 'trim', d.w, t, d.d, 0, d.h - t / 2, 0, 0.002, 1.2);              // 천판
      box(w, 'trim', t, d.h - t * 2, d.d, -hw + t / 2, d.h / 2, 0, 0.002, 1.2); // 좌
      box(w, 'trim', t, d.h - t * 2, d.d, hw - t / 2, d.h / 2, 0, 0.002, 1.2);  // 우
      box(w, 'trim', d.w - t * 2, d.h - t * 2, t, 0, d.h / 2, d.d / 2 - t / 2, 0.002, 1.2); // 뒤판

      // --- 안쪽 (열린 짝으로 보이는 것) ---
      w.part('선반');
      box(w, 'trim', d.w - t * 2, 0.022, inner - 0.02, 0, d.shelfY, -0.01, 0.002, 1.0);
      w.part('옷걸이 봉');
      const rod = new THREE.CylinderGeometry(0.014, 0.014, d.w - t * 2, 10);
      rod.rotateZ(Math.PI / 2);
      rod.translate(0, d.shelfY - 0.12, -0.02);
      w.add(rod, 'steel', 0.3);
      // --- 걸린 옷·개어 둔 옷 (clothes 옵션) ---
      if (d.clothes) {
      w.part('걸린 옷');
      const cloths: Slot[] = ['fabric', 'redcloth', 'painted', 'leather', 'fabric'];
      const nH = 5;
      for (let i = 0; i < nH; i++) {
        const x = -(d.w - 0.4) / 2 + (i + 0.5) * ((d.w - 0.4) / nH);
        const len = 0.7 + (i % 3) * 0.08;
        const g = bevelBox(0.1 + (i % 2) * 0.03, len, 0.24 - (i % 2) * 0.05, 0.02, 2);
        g.translate(x, d.shelfY - 0.14 - len / 2, -0.02);
        w.add(g, cloths[i % cloths.length], 0.6);
        const hook = new THREE.CylinderGeometry(0.004, 0.004, 0.06, 6); hook.translate(x, d.shelfY - 0.12 - 0.03, -0.02); w.add(hook, 'steel', 0.2);
      }
      w.part('개어 둔 옷');
      for (let i = 0; i < 2; i++) { const g = bevelBox(0.3, 0.05, 0.26, 0.012, 2); g.translate(-d.w / 2 + 0.3, d.shelfY + 0.011 + 0.025 + i * 0.05, -0.02); w.add(g, cloths[(i + 2) % cloths.length], 0.6); }
      }

      // --- 문 두 짝 ---
      // 오른짝은 닫혀 있고 왼짝은 열려 있다. 열린 각도만큼 힌지에서 돌린다.
      const leafW = (d.w - t * 2) / 2;
      const leafH = d.h - t * 2 - 0.008;
      const leafT = 0.022;
      const doorZ = -d.d / 2 + leafT / 2;

      const leaf = (label: string, hingeX: number, dir: number, ang: number) => {
        w.movable(label, () => {
        // 힌지를 원점으로 만들고 돌린 뒤 제자리로 옮긴다
        const mk = (sx: number, sy: number, sz: number, ox: number, oy: number, oz: number) => {
          const g = bevelBox(sx, sy, sz, 0.002);
          g.translate(ox, oy, oz);
          return g;
        };
        const parts: THREE.BufferGeometry[] = [
          mk(leafW, leafH, leafT, (dir * leafW) / 2, 0, 0),
          // 패널 턱 — 문짝이 판때기로 보이지 않게 한다
          mk(leafW - 0.14, leafH - 0.14, 0.006, (dir * leafW) / 2, 0, -leafT / 2 - 0.002),
        ];
        for (const g of parts) {
          g.rotateY(dir * ang);
          g.translate(hingeX, d.h / 2, doorZ);
          w.add(g, 'trim', 1.1);
        }
        // 손잡이 — 힌지 반대쪽 끝
        const knob = new THREE.CylinderGeometry(0.012, 0.012, 0.09, 10);
        knob.rotateZ(Math.PI / 2);
        knob.translate(dir * (leafW - 0.06), 0, -leafT / 2 - 0.02);
        knob.rotateY(dir * ang);
        knob.translate(hingeX, d.h / 2, doorZ);
        w.add(knob, 'brass', 0.2);
        });
      };
      // 이름에 상태를 적지 않는다 — 이제 프롭이 여닫으므로 「(열림)」은 거짓이 될 수 있다
      leaf('왼짝', -hw + t, 1, d.ajar);
      leaf('오른짝', hw - t, -1, d.ajarR);
    },
  },

  {
    id: 'nail_drawer_chest',
    name: '문 옆 서랍장',
    collection: 'sahol',
    category: '이야기',
    note:
      '**앞판 아래 25cm 에 손톱 자국 네 줄이 있는 물건이다.** 그 높이가 단서 본문에 ' +
      '숫자로 박혀 있으므로 서랍 높이를 함부로 바꾸면 글이 거짓말이 된다 — ' +
      '맨 아래 서랍 앞판이 y 0.16~0.34 를 덮게 잡아 25cm 가 그 판 위에 오게 했다. ' +
      '위는 휴대폰 자리라 상판이 비어 있어야 한다.',
    /* 서랍 셋이 각각 앞(−Z)으로 0.3m 미끄러진다. 안에 물건을 넣을 수 있게 담는 곳도 준다.
     * 값은 dims(w 0.52 · h 0.7 · d 0.42 · n 3 · top 0.028 · base 0.08)에서 계산한 것을 적는다 —
     * openable 은 정적이라 dims 를 못 읽는다. 칸 높이 each = (0.7−0.028−0.08)/3 = 0.197 */
    openable: [
      { part: '서랍1', slide: [0, 0, -0.3], inside: { w: 0.46, h: 0.15, d: 0.34, at: [0, 0.18, 0.01] } },
      { part: '서랍2', slide: [0, 0, -0.3], inside: { w: 0.46, h: 0.15, d: 0.34, at: [0, 0.38, 0.01] } },
      { part: '서랍3', slide: [0, 0, -0.3], inside: { w: 0.46, h: 0.15, d: 0.34, at: [0, 0.58, 0.01] } },
    ],
    dims: {
      w: 0.52,
      h: 0.7,
      d: 0.42,
      /** 서랍 단수 */
      n: 3,
      /** 상판 두께 */
      top: 0.028,
      /** 손톱 자국이 있어야 하는 높이 (참고값 — 아래 서랍 앞판이 이 높이를 덮어야 한다) */
      nailY: 0.25,
      /** 서랍 속 살림(천·테이프·영수증…). 0 이면 빈 서랍 */
      stocked: 1,
    },
    build(w, d) {
      const hd = d.d / 2;

      /* **서랍 셋을 이 조각이 안는다** (2026-09-11, Chris: 「서랍이 지금 열리고 닫히는 애가
       * 하나 뿐임」). 예전엔 골조만 짓고 서랍은 `chest_drawer` 프롭이 따로 그려서, 프롭을 하나만
       * 달면 한 칸만 열리고 나머지 두 칸은 뻥 뚫린 채였다. 이제 칸마다 서랍이 제 그룹으로 있고
       * 프롭은 `attach`+`part` 로 「몇 번째 칸인지」만 말한다. */
      const T = 0.012;
      const base = 0.08;
      const span = d.h - d.top - base;
      const each = span / d.n;

      w.part('몸통');
      for (const sx of [-1, 1]) {
        box(w, 'trim', T, span, d.d - 0.02, sx * (d.w / 2 - T / 2), base + span / 2, 0.01, 0.003, 1.2);
      }
      box(w, 'trim', d.w - T * 2, span, T, 0, base + span / 2, hd - 0.02, 0.003, 1.2);
      w.part('선반');
      for (let i = 0; i <= d.n; i++) {
        box(w, 'trim', d.w - T * 2, T, d.d - 0.03, 0, base + each * i, 0.01, 0.002, 1.0);
      }
      /* 칸마다 서랍 한 짝. 앞판 + 얕은 상자(옆판 둘·바닥·뒤판) — 앞판만 만들면 물건을 못 담고
       * 안에 둔 종이가 바닥을 뚫고 나온다(2026-09-10 에 겪은 것). 손잡이는 놋쇠 막대로 한 벌. */
      const DT = 0.008, gap = 0.006;
      for (let i = 0; i < d.n; i++) {
        const y0 = base + each * i + T;           // 그 칸 선반 윗면
        const dh = each - T - gap;                // 서랍 높이
        const dw = d.w - T * 2 - gap;
        const dd = d.d - 0.05;
        w.movable(`서랍${i + 1}`, () => {
          const face = bevelBox(dw + 0.01, dh, 0.02, 0.002);
          face.translate(0, y0 + dh / 2, -d.d / 2 + 0.01);
          w.add(face, 'trim', 1.0);
          const pull = bevelBox(dw * 0.42, 0.014, 0.018, 0.002);
          pull.translate(0, y0 + dh / 2, -d.d / 2 - 0.008);
          w.add(pull, 'brass', 0.2);
          for (const sx2 of [-1, 1] as const) {
            const side = bevelBox(DT, dh - 0.02, dd, 0.002);
            side.translate(sx2 * (dw / 2 - DT / 2), y0 + dh / 2, 0.01);
            w.add(side, 'trim', 0.8);
          }
          const bot = bevelBox(dw - DT * 2, DT, dd, 0.002);
          bot.translate(0, y0 + DT / 2 + 0.004, 0.01);
          w.add(bot, 'trim', 0.8);
          const back = bevelBox(dw - DT * 2, dh - 0.02, DT, 0.002);
          back.translate(0, y0 + dh / 2, d.d / 2 - 0.04);
          w.add(back, 'trim', 0.8);

          /* **속** (2026-09-16, Chris: 「열면 속이 있어야」 — 빈 서랍 금지). 칸마다 다른 살림.
           * 가운데 앞쪽은 비워 둔다 — 담는 곳(`inside`)에 넣은 물건이 거기 앉는다. `dims.stocked: 0` 이면 비운다 */
          if ((d.stocked ?? 1) > 0) {
            const fy = y0 + DT + 0.004;                 // 서랍 바닥 윗면
            const bz = 0.01 + dd / 2 - 0.02;            // 뒤판 바로 앞
            const tone = (g: THREE.BufferGeometry, c: string) => { paintGeometry(g, c); return g; };
            if (i === 0) {
              // 아래 칸 — 갠 천 두 무더기(수건·행주)와 노끈 뭉치
              for (const [x, cols, n] of [[-0.14, ['#c9bfae', '#a9b7c6', '#d8cfc0'], 3], [0.12, ['#b8a48e', '#cfd6dc'], 2]] as const) {
                for (let k = 0; k < n; k++) {
                  const g = bevelBox(0.19 - k * 0.006, 0.026, 0.14, 0.006, 2);
                  g.rotateY((k % 2 ? -1 : 1) * 0.04);
                  g.translate(x, fy + 0.013 + k * 0.026, bz - 0.08);
                  w.add(tone(g, cols[k]), 'fabric', 0.6);
                }
              }
              const twine = new THREE.TorusGeometry(0.03, 0.011, 6, 14);
              twine.rotateX(Math.PI / 2); twine.translate(0.16, fy + 0.011, -0.06);
              w.add(tone(twine, '#b39a6a'), 'fabric', 0.4);
            } else if (i === 1) {
              // 가운데 칸 — 테이프 두 롤, 건전지 넉 개, 초 한 자루, 성냥갑
              for (const [x, z, r] of [[-0.17, bz - 0.09, 0.032], [-0.1, bz - 0.15, 0.024]] as const) {
                const roll = new THREE.TorusGeometry(r, r * 0.33, 6, 18);
                roll.rotateX(Math.PI / 2); roll.translate(x, fy + r * 0.33, z);
                w.add(tone(roll, r > 0.03 ? '#8d8f93' : '#d9c26b'), 'plastic', 0.3);
              }
              for (let k = 0; k < 4; k++) {
                const cell = new THREE.CylinderGeometry(0.007, 0.007, 0.05, 8);
                cell.rotateZ(Math.PI / 2); cell.translate(0.14, fy + 0.007, bz - 0.04 - k * 0.016);
                w.add(tone(cell, k % 2 ? '#2b2b2e' : '#b48a3c'), 'steel', 0.3);
              }
              const candle = new THREE.CylinderGeometry(0.011, 0.011, 0.16, 8);
              candle.rotateZ(Math.PI / 2); candle.rotateY(0.12); candle.translate(0.02, fy + 0.011, bz - 0.03);
              w.add(tone(candle, '#f1e9d6'), 'paint', 0.3);
              const matches = bevelBox(0.055, 0.016, 0.036, 0.002);
              matches.rotateY(0.5); matches.translate(0.17, fy + 0.008, bz - 0.13);
              w.add(tone(matches, '#8c4a3a'), 'cardboard', 0.5);
            } else {
              // 위 칸 — 고무줄로 묶은 영수증·고지서 뭉치, 볼펜 둘, 안경집
              for (let k = 0; k < 2; k++) {
                const stack = bevelBox(0.11, 0.014, 0.19, 0.002);
                stack.rotateY(k ? 0.25 : -0.08); stack.translate(-0.16 + k * 0.05, fy + 0.007 + k * 0.014, bz - 0.11);
                w.add(tone(stack, k ? '#efe9dc' : '#e2dccc'), 'paper', 0.5);
              }
              const band = new THREE.TorusGeometry(0.026, 0.0018, 4, 12);
              band.rotateY(Math.PI / 2); band.rotateX(0.2); band.translate(-0.135, fy + 0.032, bz - 0.11);
              w.add(tone(band, '#c99d6b'), 'plastic', 0.2);
              for (let k = 0; k < 2; k++) {
                const pen = new THREE.CylinderGeometry(0.004, 0.0035, 0.14, 6);
                pen.rotateX(Math.PI / 2); pen.rotateY(0.3 + k * 0.25); pen.translate(0.06 + k * 0.03, fy + 0.004, bz - 0.09);
                w.add(tone(pen, k ? '#1f2a44' : '#c2c2c6'), 'plastic', 0.3);
              }
              const glassesCase = bevelBox(0.16, 0.036, 0.062, 0.012, 2);
              glassesCase.rotateY(0.1); glassesCase.translate(0.15, fy + 0.018, bz - 0.06);
              w.add(tone(glassesCase, '#3a2f2a'), 'leather', 0.4);
            }
          }
        });
      }
      w.part('상판');
      box(w, 'trim', d.w + 0.02, d.top, d.d + 0.016, 0, d.h - d.top / 2, 0, 0.003, 1.2);
      // 굽
      w.part('굽');
      box(w, 'trim', d.w - 0.05, base, d.d - 0.06, 0, base / 2, 0.01, 0.002, 0.8);
    },
  },


  /* ---------------------------------------------------------------- */
  /* 윤가람의 집 — 임세진에게서 물려받은 것들                            */
  /* ---------------------------------------------------------------- */
  {
    id: 'kiln',
    name: '가마',
    collection: 'sahol',
    category: '이야기',
    note:
      '12/19 낮에 노트를 태운 곳이다. 시골 도예 공방의 전기 가마 — 장작 가마로 만들면 ' +
      '「낮에 혼자 돌렸다」가 안 읽힌다. 가마를 가마로 만드는 것은 **두꺼운 통 + 위에서 ' +
      '여는 뚜껑 + 옆에 붙은 제어함**이다. 통만 두면 드럼통이고, 제어함이 있으면 기계다.',
    dims: {
      /** 바깥 지름 */
      d: 0.68,
      /** 통 높이 */
      h: 0.82,
      /** 벽 두께 — 내화벽이라 두껍다. 이게 얇으면 가마로 안 보인다 */
      t: 0.075,
      /** 다리 높이 */
      leg: 0.14,
    },
    /** 뚜껑이 뒤 경첩에서 더 젖혀진다 — 노트를 태운 안을 들여다보는 동작 (2026-09-16 상호작용) */
    openable: [
      { part: '뚜껑', swing: 1.35, axis: 'x', pivot: [0, 0.98, 0.35], inside: { w: 0.5, h: 0.7, d: 0.5, at: [0, 0.55, 0] } },
    ],
    build(w, d) {
      const r = d.d / 2;
      const y0 = d.leg;

      // --- 통 (두꺼운 내화벽) ---
      // 위가 열리므로 폐단면 lathe 로 만든다 — 열린 실린더는 투과된다.
      w.part('통');
      const wall = lathe(
        [
          [r, 0],
          [r, d.h],
          [r - d.t, d.h],
          [r - d.t, 0.04],
          [r, 0],
        ],
        22,
      );
      wall.translate(0, y0, 0);
      w.add(wall, 'plaster', 0.6);
      // 바닥 (내화 벽돌)
      w.part('바닥');
      tube(w, 'plaster', r - d.t, r - d.t, 0.05, 0, y0, 0, 22, 0.4);
      // 안쪽 그을음
      w.part('그을음');
      const soot = lathe(
        [
          [r - d.t - 0.002, 0.05],
          [r - d.t - 0.002, d.h - 0.03],
          [r - d.t - 0.01, d.h - 0.03],
          [r - d.t - 0.01, 0.05],
          [r - d.t - 0.002, 0.05],
        ],
        22,
      );
      soot.translate(0, y0, 0);
      w.add(soot, 'ebony', 0.5);

      // --- 강철 밴드 (통을 조인다) ---
      w.part('밴드');
      for (const by of [d.h * 0.18, d.h * 0.62]) {
        tube(w, 'steel', r + 0.012, r + 0.012, 0.034, 0, y0 + by, 0, 22, 0.3);
      }

      // --- 뚜껑 (위로 여는 것 — 살짝 들려 있다) ---
      w.movable('뚜껑', () => {
      w.part('뚜껑');
      const lid = lathe(
        [
          [0, 0.06],
          [r + 0.016, 0.05],
          [r + 0.016, 0],
          [0, 0],
        ],
        22,
      );
      lid.rotateX(-0.16);            // 경첩에서 조금 들린 상태
      lid.translate(0, y0 + d.h + 0.03, 0.03);
      w.add(lid, 'steel', 0.5);
      w.part('뚜껑 손잡이');
      // 뚜껑 돔의 꼭대기는 로컬 y 0.06 이다. +0.14 에 두면 80mm 위 공중에 뜬다.
      // z 도 0(꼭대기)에 둔다 — 돔이라 -r*0.5 쪽은 표면이 더 낮아 다시 벌어진다.
      box(w, 'ebony', 0.16, 0.03, 0.034, 0, y0 + d.h + 0.071, 0, 0.003, 0.2);
      });
      w.part('경첩');
      box(w, 'steel', 0.11, 0.022, 0.05, 0, y0 + d.h + 0.02, r + 0.01, 0.002, 0.2);

      // --- 제어함 (가마를 기계로 만드는 부품) ---
      w.part('제어함');
      const cx = -r - 0.09;
      box(w, 'steel', 0.17, 0.3, 0.14, cx, y0 + d.h * 0.5, 0, 0.003, 0.5);
      w.part('제어판');
      box(w, 'plastic', 0.13, 0.09, 0.014, cx - 0.086, y0 + d.h * 0.6, 0, 0.002, 0.2);
      w.part('온도 표시');
      box(w, 'screen', 0.075, 0.03, 0.006, cx - 0.095, y0 + d.h * 0.62, 0, 0.001, 0.15);
      w.part('다이얼');
      for (let i = 0; i < 2; i++) {
        const dial = new THREE.CylinderGeometry(0.017, 0.017, 0.014, 12);
        dial.rotateZ(Math.PI / 2);
        dial.translate(cx - 0.092, y0 + d.h * 0.44, -0.032 + i * 0.064);
        w.add(dial, 'plastic', 0.15);
      }
      // 전선
      // 전선 — 마디 **높이보다 간격이 좁아야** 서로 닿는다. 마디 0.05 를 0.06 간격으로
      // 놓았더니 1cm 씩 벌어져 마디마다 별개 덩이가 됐다 (joint-check 가 91~136mm 로 잡았다).
      // 마디 수를 늘려 간격을 0.04 로 좁힌다.
      w.part('전선');
      const wireN = 11;
      for (let i = 0; i < wireN; i++) {
        const t = i / (wireN - 1);
        tube(
          w,
          'rubber',
          0.011,
          0.011,
          0.06,
          cx - 0.02,
          y0 + d.h * 0.34 - t * (y0 + d.h * 0.34),
          -0.06 - t * 0.12,
          8,
          0.2,
        );
      }

      // --- 다리 ---
      w.part('다리');
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
        box(w, 'steel', 0.04, d.leg, 0.04, Math.sin(a) * r * 0.72, d.leg / 2, Math.cos(a) * r * 0.72, 0.002, 0.2);
      }
    },
  },

  {
    id: 'pottery_wheel',
    name: '도예 물레',
    collection: 'sahol',
    category: '이야기',
    note:
      '임세진에게서 물려받은 것이다. 물레를 물레로 만드는 것은 **낮은 원판 + 그 아래 ' +
      '흙받이 통 + 앉는 자리와 발판** 이다. 원판만 두면 탁자로 보인다. ' +
      '작업하던 물건이라 원판에 흙이 말라붙어 있어야 한다.',
    dims: {
      /** 물레판 지름 */
      plate: 0.34,
      /** 작업 높이 */
      h: 0.62,
      /** 흙받이 통 지름 */
      basin: 0.56,
      /** 프레임 폭 */
      w: 0.66,
      d: 0.72,
    },
    /* 물레판이 축을 중심으로 돌아간다 — 판 위 흙이 같이 (2026-09-16) */
    openable: [{ part: '물레판', swing: 6.3, axis: 'y', pivot: [0, 0.6, 0] }],
    build(w, d) {
      const pr = d.plate / 2;
      const br = d.basin / 2;
      const hw = d.w / 2;
      const hd = d.d / 2;

      // --- 프레임 ---
      w.part('프레임');
      for (const [fx, fz] of [
        [-hw + 0.035, -hd + 0.035],
        [hw - 0.035, -hd + 0.035],
        [-hw + 0.035, hd - 0.035],
        [hw - 0.035, hd - 0.035],
      ] as const) {
        box(w, 'trim', 0.05, d.h - 0.06, 0.05, fx, (d.h - 0.06) / 2, fz, 0.003, 0.8);
      }
      // 아래 묶음대 — **네 방향 다 있어야 한다.** z 방향만 두면 좌우 다리 쌍이 각각
      // 따로 묶이고 앞뒤가 안 이어져서, 뒤쪽 다리 둘이 몸통에서 600mm 떨어진 별개
      // 덩이가 된다(joint-check). x 방향 묶음대가 그 둘을 잇고, 겸해서 모터를 받는다.
      w.part('묶음대');
      for (const fz of [-hd + 0.035, hd - 0.035]) {
        box(w, 'trim', d.w - 0.06, 0.035, 0.04, 0, 0.16, fz, 0.002, 0.6);
      }
      for (const fx of [-hw + 0.035, hw - 0.035]) {
        box(w, 'trim', 0.04, 0.035, d.d - 0.06, fx, 0.16, 0, 0.002, 0.6);
      }

      // --- 흙받이 통 (물레판 아래를 받는 얕은 통) ---
      // 위가 열린 통 — 폐단면 lathe
      w.part('흙받이');
      const basin = lathe(
        [
          [br, 0],
          [br, 0.1],
          [br - 0.014, 0.1],
          [br - 0.014, 0.012],
          [br, 0],
        ],
        20,
      );
      basin.translate(0, d.h - 0.16, 0);
      w.add(basin, 'plastic', 0.5);
      w.part('흙받이 바닥');
      tube(w, 'plastic', br - 0.014, br - 0.014, 0.014, 0, d.h - 0.16, 0, 20, 0.3);
      // 말라붙은 흙
      w.part('마른 흙');
      tube(w, 'earthen', br - 0.03, br - 0.03, 0.016, 0, d.h - 0.146, 0, 20, 0.3);

      // --- 물레판 ---
      w.part('물레판');
      /* 판과 그 위의 흙이 같이 돈다 — 물레는 돌리는 것이다 (2026-09-16) */
      w.movable('물레판', () => {
      tube(w, 'steel', pr, pr, 0.022, 0, d.h - 0.022, 0, 24, 0.4);
      // 판 가운데 축 — 길이 0.2 면 상단이 0.58 에서 끝나 물레판 하단(0.598)에 18mm
      // 못 미친다. 판을 관통하게 0.24 로 뽑는다 (tube 의 y 는 **하단** 기준이다).
      w.part('축');
      tube(w, 'steel', 0.026, 0.026, 0.24, 0, d.h - 0.24, 0, 12, 0.3);
      // 판에 말라붙은 흙 — 작업하던 물건이라는 증거
      w.part('판의 흙');
      tube(w, 'earthen', pr * 0.62, pr * 0.72, 0.01, 0, d.h, 0, 20, 0.25);
      });

      // --- 발판 (다리로 속도를 조절한다) ---
      w.part('발판');
      const pedal = bevelBox(0.24, 0.022, 0.13, 0.002);
      pedal.rotateX(0.1);
      pedal.translate(0, 0.1, -hd - 0.04);
      w.add(pedal, 'trim', 0.4);
      // 링크는 발판(z -0.4)과 **묶음대**(z -0.325, y 0.1425~0.1775)를 이어야 한다.
      // z 로 16mm 짜리 막대만 두면 둘 중 어느 것과도 z 가 겹치지 않아 발판이 프레임에서
      // 25mm 떨어진 덩이가 된다 (앉는 자리를 뺀 뒤 드러났다).
      w.part('발판 링크');
      box(w, 'steel', 0.016, 0.09, 0.1, 0.07, 0.15, -0.37, 0.001, 0.2);

      // --- 모터 ---
      w.part('모터');
      // y 0.28 에 두면 하단이 0.21 이라 묶음대 상단(0.1775)에서 32mm 떠서 모터·벨트가
      // 공중에 뜬 덩이가 된다. 하단이 묶음대에 물리도록 0.24 로 내린다.
      const motor = new THREE.CylinderGeometry(0.07, 0.07, 0.13, 14);
      motor.rotateZ(Math.PI / 2);
      motor.translate(-hw + 0.1, 0.24, 0);
      w.add(motor, 'steel', 0.4);
      w.part('벨트');
      const belt = new THREE.TorusGeometry(0.1, 0.008, 8, 20);
      belt.rotateY(Math.PI / 2);
      belt.translate(-hw + 0.16, 0.26, 0);
      w.add(belt, 'rubber', 0.2);

      // 앉는 자리는 **여기 넣지 않는다.** 한 조각 = 한 덩이 규칙을 지키려고 스툴을
      // 프레임에 붙였더니 물레 정면을 가로막고 다리에 용접된 것처럼 보였다 — 여섯
      // 방향 시트에서 명백했다. 앉는 자리가 필요하면 맵에서 chair 를 앞에 놓는다.
      // 물레는 물레만으로 완결된 물건이다.
    },
  },

  /* ---------------------------------------------------------------- */
  /* 2순위 — 방이 방으로 안 보이는 것                                   */
  /* ---------------------------------------------------------------- */
  {
    id: 'steel_cabinet',
    name: '철제 서류 캐비닛',
    collection: 'sahol',
    category: '이야기',
    note:
      '사무실의 cabinet_a·cabinet_b(2차부터 열린다)와 배정호 집의 bae_cabinet — ' +
      '손으로 적은 장부가 든 곳이다. 캐비닛을 캐비닛으로 만드는 것은 몸통이 아니라 ' +
      '**서랍마다 붙은 라벨 홀더**다. 라벨이 없으면 그냥 철제 함이고, 라벨이 있으면 ' +
      '「누가 무엇을 분류해 넣어 둔 것」이 된다. 레터 규격 4단(폭 470 · 깊이 620).',
    dims: {
      w: 0.47,
      h: 1.32,
      d: 0.62,
      /** 서랍 단수 */
      n: 4,
      /** 판 두께 */
      t: 0.018,
      /** 굽 높이 */
      base: 0.06,
    },
    /* 넉 단이 각각 앞(-Z)으로 빠진다. 서랍 깊이 0.60 에서 0.42 를 빼면 속이 다 보이면서
     * 상자가 레일에서 떨어지지는 않는다. 담는 곳은 2단 — 손으로 적은 장부가 든 칸이다.
     * 축·거리는 정적이라 기본 dims(h 1.32 · base 0.06 · n 4)에서 나온 수를 적는다:
     * span = 1.24 · each = 0.31 · 2단 바닥 y = 0.06 + 0.01 + 0.31 = 0.38 */
    openable: [
      { part: '1단', slide: [0, 0, -0.42] },
      { part: '2단', slide: [0, 0, -0.42], inside: { w: 0.4, h: 0.2, d: 0.5, at: [0, 0.5, 0] } },
      { part: '3단', slide: [0, 0, -0.42] },
      { part: '4단', slide: [0, 0, -0.42] },
    ],
    build(w, d) {
      const hw = d.w / 2;
      const hd = d.d / 2;

      /* --- 몸통 ---
       * **통짜 상자로 두면 서랍을 빼도 그 자리가 막혀 있다**(2026-09-12 택배상자에서 겪은 것).
       * 옆·위·아래·뒤만 남기고 앞을 튼 껍데기로 짓는다 — 빠져나온 서랍 뒤로 빈 칸이 보여야 한다 */
      w.part('몸통');
      const CT = d.t;
      const bodyH = d.h - d.base;
      for (const [sx, sy, sz, px, py, pz] of [
        [CT, bodyH, d.d, -hw + CT / 2, d.base + bodyH / 2, 0],
        [CT, bodyH, d.d, hw - CT / 2, d.base + bodyH / 2, 0],
        [d.w, CT, d.d, 0, d.base + bodyH - CT / 2, 0],
        [d.w, CT, d.d, 0, d.base + CT / 2, 0],
        [d.w - CT * 2, bodyH - CT * 2, CT, 0, d.base + bodyH / 2, hd - CT / 2],
      ] as const) box(w, 'steel', sx, sy, sz, px, py, pz, 0.003, 1.0);
      w.part('굽');
      // 철제 캐비닛은 바닥에 바로 닿지 않는다 — 얇은 굽이 있어야 「놓인 것」으로 보인다
      box(w, 'ebony', d.w - 0.03, d.base, d.d - 0.03, 0, d.base / 2, 0, 0.002, 0.4);

      /* --- 서랍 넉 단 ---
       * 앞판만 붙여 두면 「열리는 것처럼 생긴 철판」이다(Chris 2026-09-12: 여닫이를 늘려라).
       * 형태는 `lockDrawerBuild` 를 그대로 부르고, 이 캐비닛의 정체인 **라벨 홀더**만 덧붙인다 */
      const span = d.h - d.base - 0.02;
      const each = span / d.n;
      /* 앞판은 몸통을 덮을 만큼 넓게, 상자는 **안쪽 폭(w − 2t = 0.434)보다 좁게**.
       * 상자가 벽을 뚫으면 면이 겹쳐 옆면에 얼룩이 진다(2026-09-12 side 컷) */
      const dd: Record<string, number> = { w: d.w - 0.012, boxW: d.w - d.t * 2 - 0.012, h: each - 0.006, d: d.d - 0.02, face: 0.016 };
      for (let i = 0; i < d.n; i++) {
        const y0 = d.base + 0.01 + each * i;
        w.movable(`${i + 1}단`, () => {
          w.at(0, y0, 0, () => {
            // 라벨 홀더는 `lockDrawerBuild` 가 손잡이 바로 아래에 이미 단다 — 여기서 또 달면 두 장이 된다
            lockDrawerBuild(w, dd);
          });
        });
      }

      // --- 잠금 (열쇠 구멍) ---
      // 「2차부터 열린다」가 형태로 보여야 한다
      w.part('잠금');
      const lock = new THREE.CylinderGeometry(0.014, 0.014, 0.012, 12);
      lock.rotateX(Math.PI / 2);
      lock.translate(hw - 0.05, d.h - 0.07, -hd - 0.012);
      w.add(lock, 'brass', 0.15);
    },
  },

  {
    id: 'mfp_printer',
    name: '복합기',
    collection: 'sahol',
    category: '이야기',
    note:
      '**없으면 안 되는 물건이다.** 배정호가 12/17 00:30 과 01:44 에 정산서를 뽑았다는 ' +
      '로그의 물리 근거다. 복합기를 복합기로 만드는 것은 **위로 여는 스캐너 뚜껑 + ' +
      '아래 용지함 + 그 사이의 배출 트레이** 셋이다. 배출 트레이에 종이가 몇 장 남아 ' +
      '있어야 「뽑았다」가 읽힌다 — 비어 있으면 그냥 사무기기다.',
    /** 전원선이 뒤로 늘어져 바닥에 닿는다 */
    sunk: true,
    dims: {
      w: 0.47,
      h: 0.41,
      d: 0.44,
      /** 스캐너 뚜껑이 들린 각도 */
      lidAjar: 0.12,
    },
    /* 스캐너 뚜껑은 뒤(+Z) 경첩 — lidAjar 만큼 들린 채 구워 있고 E 로 크게 연다.
     * 용지함은 -Z 로 당기는 서랍, 배출 트레이의 종이는 집어 든다 */
    openable: [
      { part: '뚜껑', swing: 1.5, axis: 'x', pivot: [0, 0.374, 0.22], inside: { w: 0.38, h: 0.05, d: 0.34, at: [0, 0.365, 0] } },
      { part: '용지함', slide: [0, 0, -0.3], inside: { w: 0.4, h: 0.07, d: 0.3, at: [0, 0.14, -0.02] } },
      { part: '뽑힌 종이', slide: [0, 0.05, -0.25] },
    ],
    build(w, d) {
      const hw = d.w / 2;
      const hd = d.d / 2;

      // --- 아래 몸통 (용지함이 들어간다) ---
      w.part('몸통');
      box(w, 'plastic', d.w, d.h * 0.44, d.d, 0, (d.h * 0.44) / 2, 0, 0.004, 0.8);
      w.part('용지함');
      // 앞으로 살짝 나온 서랍 — 종이가 든 곳
      w.movable('용지함', () => {
        box(w, 'plastic', d.w - 0.03, d.h * 0.2, 0.02, 0, d.h * 0.14, -hd - 0.008, 0.002, 0.4);
        box(w, 'plastic', 0.13, 0.014, 0.018, 0, d.h * 0.14, -hd - 0.022, 0.002, 0.2);
      });

      // --- 배출 트레이 (몸통 위의 오목한 자리) ---
      // 이 자리가 비어 있으면 「뽑았다」가 안 읽힌다
      w.part('배출 트레이');
      box(w, 'plastic', d.w - 0.04, 0.014, d.d - 0.09, 0, d.h * 0.44 + 0.007, -0.01, 0.002, 0.5);
      // 트레이 앞턱 — 종이가 흘러내리지 않게 세운 것
      box(w, 'plastic', d.w - 0.04, 0.026, 0.014, 0, d.h * 0.44 + 0.02, -hd + 0.032, 0.002, 0.3);
      w.part('뽑힌 종이');
      // 몇 장 겹쳐 있다. 살짝 어긋나게 놓아 「여러 번 뽑았다」로 보인다
      w.movable('뽑힌 종이', () => {
        for (let i = 0; i < 4; i++) {
        box(
          w,
          'paper',
          0.21,
          0.0026,
          0.297,
          (i - 1.5) * 0.006,
          d.h * 0.44 + 0.016 + i * 0.0028,
          -0.01 + (i % 2 === 0 ? 0.004 : -0.004),
          0.0005,
          0.35,
        );
        }
      });

      // --- 스캐너 상부 ---
      w.part('스캐너');
      const scanY = d.h * 0.44 + 0.055;
      box(w, 'plastic', d.w, d.h * 0.3, d.d, 0, scanY + (d.h * 0.3) / 2, 0, 0.004, 0.8);
      w.part('스캐너 유리');
      box(w, 'pane', d.w - 0.06, 0.005, d.d - 0.07, 0, scanY + d.h * 0.3 - 0.002, 0, 0.001, 0.3);
      // **스캐너를 받치는 뒤 기둥 둘.** 몸통 상단(0.44h)과 스캐너 하단(0.44h+0.055)
      // 사이 55mm 가 비어서 상부가 공중에 떠 있었다. 실제 복합기도 뒤쪽 기둥으로만
      // 받치고 앞은 열어 둔다 — 그 틈이 배출 트레이다.
      w.part('스캐너 받침');
      for (const sx of [-1, 1]) {
        box(w, 'plastic', 0.07, 0.058, 0.09, sx * (hw - 0.06), scanY - 0.026, hd - 0.06, 0.003, 0.7);
      }

      // --- 뚜껑 (위로 여는 것 — 살짝 들려 있다) ---
      w.part('뚜껑');
      w.movable('뚜껑', () => {
        const lid = bevelBox(d.w, 0.03, d.d, 0.003);
        lid.translate(0, 0, -d.d / 2);          // 힌지를 뒤로
        lid.rotateX(-d.lidAjar);
        lid.translate(0, scanY + d.h * 0.3 + 0.016, hd);
        w.add(lid, 'plastic', 0.7);
      });

      // --- 조작 패널 ---
      w.part('패널');
      const panel = bevelBox(0.18, 0.014, 0.11, 0.002);
      panel.rotateX(-0.42);
      panel.translate(hw - 0.12, scanY + d.h * 0.3 - 0.01, -hd + 0.06);
      w.add(panel, 'plastic', 0.3);
      w.part('화면');
      const scr = bevelBox(0.078, 0.004, 0.042, 0.001);
      scr.rotateX(-0.42);
      scr.translate(hw - 0.12, scanY + d.h * 0.3 + 0.001, -hd + 0.058);
      w.add(scr, 'screen', 0.2);

      // --- 전원선 ---
      // 몸통 뒤 아래로 짧게 흘린다. 마디를 길게 늘어놓으면 세로 원통이 계단처럼 서서
      // 공중에 뜬 것으로 보인다 (처음에 0.5m 를 뒤로 뻗었다가 그렇게 됐다).
      w.part('전원선');
      for (let i = 0; i < 5; i++) {
        const t = i / 4;
        const seg = new THREE.CylinderGeometry(0.008, 0.008, 0.045, 8);
        seg.rotateX(Math.PI / 2 - t * 0.9);
        seg.translate(-hw + 0.06, d.h * 0.16 - t * d.h * 0.14, hd + 0.014 + t * 0.032);
        w.add(seg, 'rubber', 0.2);
      }
    },
  },

  {
    id: 'fax_machine',
    name: '팩스',
    collection: 'sahol',
    category: '이야기',
    note:
      '12/20 03:00 에 군 보건소에서 부검 소견이 여기로 온다 (county_fax). ' +
      '**수화기가 팩스를 팩스로 만든다** — 수화기가 없으면 작은 프린터와 구별되지 않는다. ' +
      '받은 종이가 배출 슬롯에 물려 있어야 「새벽에 뭔가 들어왔다」가 형태로 보인다.',
    dims: {
      w: 0.36,
      h: 0.19,
      d: 0.29,
    },
    /* 수화기를 들어 올리고, 슬롯에 물린 종이를 앞으로 뽑는다 */
    openable: [
      { part: '수화기', slide: [0, 0.08, 0] },
      { part: '받은 종이', slide: [0, -0.02, -0.22] },
    ],
    build(w, d) {
      const hw = d.w / 2;
      const hd = d.d / 2;

      // --- 몸통 ---
      // 뒤가 높고 앞이 낮은 쐐기 — 팩스 실루엣이 그렇다
      w.part('몸통');
      box(w, 'plastic', d.w, d.h * 0.62, d.d, 0, (d.h * 0.62) / 2, 0, 0.004, 0.6);
      w.part('뒤 등판');
      box(w, 'plastic', d.w, d.h * 0.5, 0.07, 0, d.h * 0.5, hd - 0.035, 0.004, 0.5);

      // --- 수화기 (정체를 결정하는 부품) ---
      w.part('수화기');
      const cx = -hw + 0.055;
      // 몸통 왼쪽에 얹힌 자리
      box(w, 'plastic', 0.1, 0.022, 0.2, cx, d.h * 0.62 + 0.011, 0.005, 0.003, 0.3);
      // 수화기 본체 — 양 끝이 두껍고 가운데가 얇다
      w.movable('수화기', () => {
        box(w, 'plastic', 0.062, 0.042, 0.062, cx, d.h * 0.62 + 0.045, -0.06, 0.004, 0.25);
        box(w, 'plastic', 0.062, 0.042, 0.062, cx, d.h * 0.62 + 0.045, 0.068, 0.004, 0.25);
        box(w, 'plastic', 0.05, 0.03, 0.13, cx, d.h * 0.62 + 0.042, 0.004, 0.003, 0.25);
      });
      // 나선 코드
      w.part('코드');
      for (let i = 0; i < 7; i++) {
        const t = i / 6;
        tube(w, 'rubber', 0.007, 0.007, 0.022, cx - 0.03 - t * 0.05, d.h * 0.5 - t * d.h * 0.4, 0.1 + t * 0.05, 8, 0.15);
      }

      // --- 키패드 ---
      w.part('키패드');
      for (let r = 0; r < 4; r++) {
        for (let c = 0; c < 3; c++) {
          box(
            w,
            'plastic',
            0.019,
            0.006,
            0.014,
            0.03 + c * 0.026,
            d.h * 0.62 + 0.004,
            -0.055 + r * 0.022,
            0.001,
            0.1,
          );
        }
      }
      w.part('표시창');
      box(w, 'screen', 0.075, 0.005, 0.02, 0.075, d.h * 0.62 + 0.004, -0.086, 0.001, 0.15);

      // --- 배출 슬롯 + 받은 종이 ---
      // 새벽에 들어온 것이 물려 있어야 한다
      w.part('배출 슬롯');
      box(w, 'ebony', d.w - 0.08, 0.008, 0.014, 0, d.h * 0.5, -hd + 0.012, 0.001, 0.2);
      w.part('받은 종이');
      w.movable('받은 종이', () => {
        const sheet = bevelBox(0.21, 0.0022, 0.24, 0.0004);
      // **앞끝이 아래로 처져야** 한다. rotateX(+0.22) 는 앞끝을 살짝 들어올려 A4 한 장이
      // 기계 앞 22cm 를 수평으로 가로지르는 모양이 됐다 — 받쳐 주는 것이 없으니 공중에
      // 뜬 판으로 보인다. 음수로 꺾고 슬롯 쪽으로 당겨 「물려서 나오다 처진」 종이로 만든다.
      // 각도 0.7 은 너무 꺾여 **뒤끝이 슬롯 위 9cm 로 올라간다.** 0.35(20도)로 눕히고,
      // 뒤끝이 정확히 배출 슬롯(y 0.5h, z -hd)에 오도록 중심을 역산한다:
      // 뒤끝 오프셋 = 길이/2 × (sin θ, cos θ) = (0.041, 0.113).
      sheet.rotateX(-0.35);
      sheet.translate(0, d.h * 0.5 - 0.041, -hd - 0.113);
        w.add(sheet, 'paper', 0.35);
      });
    },
  },

  {
    id: 'fridge',
    name: '냉장고',
    collection: 'sahol',
    category: '이야기',
    note:
      '쟁반의 밥이 나온 곳이다. 여섯이 2년째 사는 고친 분교라 새 양문형이 아니라 ' +
      '**작은 구형 2도어**가 맞다 — 위가 냉동, 아래가 냉장이고 손잡이가 세로 바다. ' +
      '문이 둘로 나뉜 선과 그 손잡이가 냉장고를 냉장고로 만든다. 한 통이면 사물함이다.',
    dims: {
      w: 0.56,
      h: 1.56,
      d: 0.6,
      /** 냉동실 비율 (위) */
      freezer: 0.3,
      /** 문 두께 */
      door: 0.055,
      /** 단열벽 두께 — 냉장고 벽은 두껍다. 얇게 하면 철제 사물함이 된다 */
      wt: 0.032,
      /** 기본 재고 — 1 이면 선반에 반찬통·우유갑 등이 구워진다. 실제 식품 조각을 넣을 방은 0 */
      stocked: 1,
    },
    /* 문 두 짝이 **왼쪽 경첩**에서 열린다(손잡이가 오른쪽이니 경첩은 반대쪽이다).
     * 세로 경첩이므로 y 축. 축 좌표는 정적이라 기본 dims(w 0.56 · h 1.56 · d 0.6 · door 0.055
     * · freezer 0.3)에서 나온 수를 적는다: x = −w/2 = −0.28 · z = −d/2 + door/2 = −0.2725
     * 냉동 문 중심 y = 1.326 · 냉장 문 중심 y = 0.5445
     * 담는 곳은 냉장실 — 쟁반의 밥이 나온 곳이다(note). 여기에 물건을 직접 넣을 수 있다 */
    openable: [
      { part: '냉장실 문', swing: 2.0, axis: 'y', pivot: [-0.28, 0.5445, -0.2725],
        inside: { w: 0.44, h: 0.42, d: 0.42, at: [0, 0.6, 0.03] } },
      { part: '냉동실 문', swing: 2.0, axis: 'y', pivot: [-0.28, 1.326, -0.2725] },
    ],
    build(w, d) {
      const hw = d.w / 2;
      const hd = d.d / 2;
      const bodyD = d.d - d.door;

      const gap = 0.006;
      const fzH = d.h * d.freezer - gap / 2;
      const frH = d.h * (1 - d.freezer) - gap / 2;
      const doorZ = -hd + d.door / 2;

      /* --- 몸통 ---
       * **통짜로 두면 문을 열어도 속이 안 보인다**(2026-09-12 택배상자에서 겪은 것).
       * 앞을 튼 껍데기 + 냉동/냉장 칸막이로 짓는다 */
      w.part('몸통');
      const WT = d.wt, bz = d.door / 2;
      for (const [sx, sy, sz, px, py] of [
        [WT, d.h, bodyD, -hw + WT / 2, d.h / 2],
        [WT, d.h, bodyD, hw - WT / 2, d.h / 2],
        [d.w, WT, bodyD, 0, d.h - WT / 2],
        [d.w, WT, bodyD, 0, WT / 2],
      ] as const) box(w, 'paint', sx, sy, sz, px, py, bz, 0.004, 1.2);
      box(w, 'paint', d.w, d.h, WT, 0, d.h / 2, bz + bodyD / 2 - WT / 2, 0.004, 1.2);
      w.part('칸막이');
      // 냉동과 냉장을 가르는 벽. 이게 없으면 위아래가 한 통이라 2도어가 거짓말이 된다
      box(w, 'paint', d.w - WT * 2, WT, bodyD - WT, 0, frH + gap / 2, bz, 0.003, 0.8);

      w.part('선반');
      /* 냉장실에 선반 셋 · 냉동실에 하나. 빈 통이면 「문 달린 상자」다 */
      for (const sy of [frH * 0.3, frH * 0.54, frH * 0.78] as const) {
        box(w, 'alu', d.w - WT * 2 - 0.004, 0.008, bodyD - WT - 0.02, 0, sy, bz - 0.006, 0.002, 0.5);
      }
      box(w, 'alu', d.w - WT * 2 - 0.004, 0.008, bodyD - WT - 0.02, 0, frH + fzH * 0.45, bz - 0.006, 0.002, 0.5);

      w.part('야채칸');
      // 맨 아래 서랍 한 칸 — 냉장고 바닥이 맨판이면 텅 빈 통으로 읽힌다
      box(w, 'alu', d.w - WT * 2 - 0.01, frH * 0.2, bodyD - WT - 0.03, 0, WT + (frH * 0.2) / 2, bz - 0.008, 0.003, 0.4);
      /* **문을 열면 든 것이 보여야 냉장고다.** 이건 「그냥 놓기만 해도 차 있는」 기본 재고다 —
       * 배치로 실제 식품 조각(food.ts: 소주·막걸리·우유팩·계란판·김치통…)을 넣을 때는 `stocked: 0` 으로 끈다 (2026-09-16) */
      if (d.stocked) {
      w.part('든 것');
      const iw = d.w - WT * 2 - 0.02, iz = bz - 0.01;
      // 1단 — 반찬통 둘, 우유갑
      for (const [dx, cw, ch, sl] of [[-0.14, 0.11, 0.055, 'plastic'], [0.0, 0.09, 0.05, 'plastic']] as const) {
        box(w, sl, cw, ch, 0.1, dx, frH * 0.3 + 0.004 + ch / 2, iz, 0.006, 0.3);
      }
      box(w, 'cardboard', 0.07, 0.19, 0.07, iw / 2 - 0.05, frH * 0.3 + 0.004 + 0.095, iz, 0.004, 0.3);
      // 2단 — 접시에 덮은 것, 병 둘
      box(w, 'porcelain', 0.17, 0.035, 0.17, -0.08, frH * 0.54 + 0.004 + 0.018, iz, 0.008, 0.3);
      for (const dx of [0.08, 0.15]) {
        const b2 = lathe([[0.028, 0], [0.03, 0.02], [0.028, 0.14], [0.013, 0.17], [0.013, 0.2], [0, 0.2]], 12);
        b2.translate(dx, frH * 0.54 + 0.004, iz); w.add(b2, 'plastic', 0.3);
      }
      // 3단 — 달걀판, 비닐봉지
      box(w, 'cardboard', 0.19, 0.045, 0.1, -0.05, frH * 0.78 + 0.004 + 0.022, iz, 0.004, 0.3);
      box(w, 'fabric', 0.12, 0.08, 0.1, 0.12, frH * 0.78 + 0.004 + 0.04, iz, 0.02, 0.3);
      // 야채칸 위 — 채소 뭉치
      box(w, 'painted', 0.16, 0.07, 0.12, -0.04, WT + frH * 0.2 + 0.035, iz, 0.025, 0.3);
      // 냉동실 — 얼린 봉지 둘
      for (const [dx, dz2] of [[-0.09, 0], [0.08, 0.02]] as const) {
        box(w, 'fabric', 0.13, 0.06, 0.11, dx, frH + fzH * 0.45 + 0.004 + 0.03, iz + dz2, 0.015, 0.3);
      }

      }
      // --- 문 두 짝 ---
      /* 문짝마다 **손잡이·상표까지 한 덩어리로** 묶는다 — 문이 열릴 때 손잡이가 제자리에
       * 남으면 판때기만 날아간 꼴이 된다 */
      w.movable('냉동실 문', () => {
        box(w, 'paint', d.w, fzH, d.door, 0, d.h - fzH / 2, doorZ, 0.004, 1.1);
        const hy = d.h - fzH / 2, hh = fzH * 0.5;
        box(w, 'alu', 0.028, hh, 0.03, hw - 0.055, hy, doorZ - d.door / 2 - 0.016, 0.003, 0.25);
        for (const sy of [hy - hh / 2 + 0.02, hy + hh / 2 - 0.02]) {
          box(w, 'alu', 0.02, 0.018, 0.016, hw - 0.055, sy, doorZ - d.door / 2 - 0.006, 0.002, 0.15);
        }
      });
      w.movable('냉장실 문', () => {
        box(w, 'paint', d.w, frH, d.door, 0, frH / 2, doorZ, 0.004, 1.1);
        const hy = frH / 2 + frH * 0.22, hh = frH * 0.34;
        box(w, 'alu', 0.028, hh, 0.03, hw - 0.055, hy, doorZ - d.door / 2 - 0.016, 0.003, 0.25);
        for (const sy of [hy - hh / 2 + 0.02, hy + hh / 2 - 0.02]) {
          box(w, 'alu', 0.02, 0.018, 0.016, hw - 0.055, sy, doorZ - d.door / 2 - 0.006, 0.002, 0.15);
        }
        // --- 상표 --- 냉장실 문에 붙어 있으므로 같이 움직인다
        box(w, 'alu', 0.1, 0.016, 0.005, -hw + 0.09, d.h - fzH - 0.06, doorZ - d.door / 2 - 0.002, 0.001, 0.15);
        /* 냉동/냉장을 갈라 보이게 하는 문 사이 선 — **이것도 문에 달려야 한다.**
         * 몸통에 두었더니 문을 연 순간 검은 띠가 앞 허공에 5cm 떠서 남았다(2026-09-12 열림 컷) */
        box(w, 'ebony', d.w, gap, 0.01, 0, frH + gap / 2, doorZ - d.door / 2 + 0.004, 0.001, 0.2);
      });

      // --- 뒤 방열 그릴 + 압축기 ---
      // 뒤가 매끈하면 가전으로 안 보인다
      w.part('방열 그릴');
      for (let i = 0; i < 9; i++) {
        box(w, 'steel', d.w - 0.1, 0.008, 0.012, 0, 0.5 + i * 0.09, hd - 0.006, 0.001, 0.2);
      }
      w.part('압축기');
      const comp = new THREE.CylinderGeometry(0.075, 0.075, 0.11, 12);
      comp.translate(0, 0.09, hd - 0.1);
      w.add(comp, 'steel', 0.3);
    },
  },

  {
    id: 'suitcase',
    /** 뚜껑이 뒤 힌지에서 더 젖혀진다 — 조각은 반쯤 열린 채 구워지고 이건 그 위에 더해진다 (2026-09-15). pivot 은 경첩선(통 뒷벽 상단, z=+d.d/2) */
    openable: [{ part: '뚜껑', swing: 1.0, axis: 'x', pivot: [0, 0.15, 0.225], inside: { w: 0.4, h: 0.12, d: 0.26, at: [0, 0.12, 0] } }],
    name: '여행 가방',
    collection: 'sahol',
    category: '이야기',
    note:
      '한서진의 반쯤 싼 짐이다. **임세진의 폰이 안쪽 주머니에 있다.** ' +
      '「반쯤 싸다 만 것」이 형태로 보여야 한다 — 그래서 눕혀서 뚜껑을 들어 올리고 ' +
      '옷을 삐져나오게 했다. 닫힌 가방은 그냥 짐이고, 열린 가방은 「떠나려던 사람」이다. ' +
      '28인치 하드케이스 기준(680 × 450 × 240).',
    dims: {
      w: 0.68,
      d: 0.45,
      /** 아래 통 높이 */
      h: 0.15,
      /** 뚜껑 높이 */
      lid: 0.09,
      /** 뚜껑이 열린 각도 */
      ajar: 0.72,
    },
    build(w, d) {
      const hw = d.w / 2;
      const hd = d.d / 2;

      // --- 아래 통 ---
      // 위가 열린 통이라 폐단면으로 만들어야 하지만, 사각이라 lathe 를 쓸 수 없다.
      // 판 다섯 장으로 짜면 각 판이 닫힌 몸체다.
      const t = 0.014;
      // **shellBox 로 짠다.** 판을 손으로 나열하면 한 장을 빼먹는다 — 실제로 뚜껑에서
      // 힌지 쪽 뒷턱을 빼먹어 열면 안이 들여다보였다. 비울 면을 반드시 적어야 하므로
      // 이 방식에서는 빼먹을 수가 없다. 통은 위(+y)만 열린다.
      w.part('아래 통');
      for (const g of shellBox(d.w, d.h, d.d, t, ['+y'])) w.add(g, 'plastic', 0.8);

      // --- 안에 든 옷 (반쯤 싼 것) ---
      w.part('옷');
      box(w, 'fabric', d.w - t * 3, d.h * 0.72, d.d - t * 3, 0, t + (d.h * 0.72) / 2, 0, 0.006, 0.9);
      // 뚜껑 밖으로 삐져나온 옷 한 겹 — 「닫지 못한 채 둔 것」
      // 판 한 장으로 두면 천이 아니라 삐져나온 **판자**로 보인다. 세 덩이를 각기 다른
      // 각도로 겹쳐야 접힌 옷가지가 된다 (걸레 머리와 같은 수법).
      w.part('삐져나온 옷');
      for (const [ox, oy, oz, rx, rz, sw] of [
        [d.w * 0.1, d.h + 0.012, -hd - 0.03, -0.34, 0.06, 0.26],
        [d.w * 0.18, d.h + 0.026, -hd - 0.012, -0.18, -0.12, 0.19],
        [d.w * 0.04, d.h + 0.03, -hd + 0.01, -0.08, 0.2, 0.15],
      ] as const) {
        const spill = bevelBox(sw, 0.042, 0.11, 0.008);
        spill.rotateX(rx);
        spill.rotateZ(rz);
        spill.translate(ox, oy, oz);
        w.add(spill, 'fabric', 0.7);
      }

      // --- 뚜껑 (뒤 힌지에서 들려 있다) ---
      // 힌지는 통 **뒷벽 상단**(0, h, +hd)이고 뚜껑은 거기서 앞(-Z)으로 뻗는다.
      // 껍데기를 힌지 기준 로컬(원점=힌지)로 옮긴 뒤 통째로 젖힌다. rotateX 의 부호를
      // 반대로 주면 뚜껑이 위로 열리지 않고 통 속으로 내려간다.
      //
      // 아래(-y)만 열린 shellBox 다 — 손으로 판을 나열했을 때 힌지 쪽 뒷턱을 빼먹어
      // 열면 그쪽이 뚫려 안이 들여다보였다. 옆판이 d.d 로 힌지까지 오므로 뒤끝 최저점이
      // 회전 후 정확히 통 뒷벽 상단에 떨어진다.
      w.part('뚜껑');
      // 뚜껑과 그 안쪽 주머니는 한 부품이다 — 따로 두면 뚜껑만 열리고 주머니가 허공에 남는다 (2026-09-15)
      w.movable('뚜껑', () => {
      for (const g of shellBox(d.w, d.lid, d.d, t, ['-y'])) {
        g.translate(0, 0, -d.d / 2);
        g.rotateX(d.ajar);
        g.translate(0, d.h, hd);
        w.add(g, 'plastic', 0.8);
      }

      // --- 경첩 ---
      // 힌지가 부품으로 존재하지 않아서 뚜껑이 「무엇에 매달려 열려 있는지」가 안 보였다.
      // 통 뒷벽 상단에 걸터앉아 뚜껑 뒤끝과 겹치는 놋쇠 경첩 둘.
      w.part('경첩');
      for (const sx of [-0.19, 0.19]) {
        box(w, 'brass', 0.09, 0.024, 0.026, sx, d.h - 0.002, hd - 0.012, 0.002, 0.2);
      }

      // --- 안쪽 주머니 (폰이 있는 곳) ---
      // 뚜껑 안쪽에 지퍼 달린 망 주머니. 이 게임에서 실제로 뒤지는 자리다
      w.part('안쪽 주머니');
      const pocket = bevelBox(d.w * 0.72, 0.008, d.d * 0.62, 0.002);
      // 뚜껑 안쪽 면은 겉면 판의 아래쪽, 즉 로컬 y = lid - t 다. 0.014 로 두면 뚜껑의
      // **열린 아래쪽**에 떠서 겉면에서 62mm 떨어진다.
      pocket.translate(0, d.lid - t - 0.005, -hd);   // 뚜껑 안쪽 면 (힌지 기준 로컬)
      pocket.rotateX(d.ajar);
      pocket.translate(0, d.h, hd);
      w.add(pocket, 'fabric', 0.5);

      // --- 손잡이 ---
      });

      w.part('손잡이');
      box(w, 'ebony', 0.17, 0.026, 0.032, 0, d.h * 0.62, -hd - 0.022, 0.004, 0.25);
      for (const sx of [-0.07, 0.07]) {
        box(w, 'ebony', 0.022, 0.02, 0.024, sx, d.h * 0.62, -hd - 0.008, 0.002, 0.15);
      }

      // --- 바퀴 넷 ---
      // **눕힌 가방**이므로 바퀴는 밑면이 아니라 **뒷면**에 온다. 밑면(y -0.02)에 달면
      // 조각 원점이 접지면인 규약상 바퀴가 바닥 밑으로 묻혀 아예 안 보인다 — 여섯 방향
      // 시트에서 가방이 바퀴 없이 바닥에 붙어 있었다.
      w.part('바퀴');
      for (const [wx, wy] of [
        [-hw + 0.09, 0.035],
        [hw - 0.09, 0.035],
        [-hw + 0.09, d.h - 0.035],
        [hw - 0.09, d.h - 0.035],
      ] as const) {
        const wheel = new THREE.CylinderGeometry(0.026, 0.026, 0.02, 10);
        wheel.rotateZ(Math.PI / 2);
        wheel.translate(wx, wy, hd + 0.024);
        w.add(wheel, 'rubber', 0.15);
      }
    },
  },

  {
    id: 'wash_bucket',
    glb: '/models/sahol/bucket_rag',   // 블렌더 GLB (tools/blender/props.py). 아래 build 는 대체이자 크기 계약
    nofront: true,
    name: '양동이와 걸레',
    collection: 'sahol',
    category: '이야기',
    note:
      'PROPS 의 mop_bucket(「양동이와 걸레」, 창고 3차부터)이 look:\'marker\' 로 형태가 비어 있다 — ' +
      '그 자리를 채운다. 04:30 에 한서진의 집 바닥을 닦은 물건이다. ' +
      '양동이를 양동이로 만드는 것은 **위로 벌어진 테이퍼와 반원 손잡이 고리**다. ' +
      '수직 원통에 고리만 달면 통이고, 아래가 좁아지면 들고 다니는 것이 된다. ' +
      '걸레가 걸쳐져 있어야 「닦았다」가 읽힌다.',
    dims: {
      /** 위 지름 */
      top: 0.3,
      /** 아래 지름 — 위보다 좁다. 이게 양동이 실루엣이다 */
      bot: 0.235,
      h: 0.29,
      /** 벽 두께 */
      t: 0.008,
    },
    /* 통 안의 걸레를 집어 낸다 (2026-09-16) */
    openable: [{ part: '걸레', slide: [0, 0, -0.06] }],
    build(w, d) {
      const rt = d.top / 2;
      const rb = d.bot / 2;

      // --- 통 ---
      // 위가 열린 통 — 폐단면 lathe. 열린 실린더로 만들면 투과된다.
      w.part('통');
      const wall = lathe(
        [
          [rb, 0],
          [rt, d.h],
          [rt - d.t, d.h],
          [rb - d.t, 0.01],
          [rb, 0],
        ],
        20,
      );
      w.add(wall, 'steel', 0.5);
      w.part('바닥');
      tube(w, 'steel', rb - d.t, rb - d.t, 0.012, 0, 0.006, 0, 20, 0.3);

      // --- 테두리 말이 ---
      // 실제 양동이는 입구를 바깥으로 말아 접는다. 이게 있으면 판금으로 읽힌다
      w.part('테두리');
      const rim = new THREE.TorusGeometry(rt + 0.004, 0.006, 6, 22);
      rim.rotateX(Math.PI / 2);
      rim.translate(0, d.h - 0.002, 0);
      w.add(rim, 'steel', 0.25);

      // --- 손잡이 고리 (정체를 결정하는 부품) ---
      // 반원으로 세워 든 상태. 눕히면 통 위에 걸친 막대로 보인다.
      // **부분 TorusGeometry 를 쓰면 안 된다** — 호를 자르면 양 끝 단면이 뚫려
      // 투과된다 (게이트가 경계 모서리 12개로 잡았다). cappedArc 가 끝을 막아 준다.
      w.part('손잡이');
      const bail = cappedArc(rt + 0.008, 0.006, Math.PI, 24, 6);
      bail.rotateY(Math.PI / 2);
      bail.translate(0, d.h - 0.01, 0);
      w.add(bail, 'steel', 0.25);
      // 고리를 통에 물리는 귀 둘
      for (const sx of [-1, 1]) {
        box(w, 'steel', 0.016, 0.03, 0.014, sx * (rt + 0.002), d.h - 0.03, 0, 0.001, 0.15);
      }

      // --- 걸레 ---
      // 통 안에 담기고 자루가 밖으로 비스듬히 나온다
      // 자루는 **테두리에 걸쳐야** 한다. 통 입구 중앙을 통과해 공중으로 뻗으면 닿는
      // 데가 없어 막대가 떠 있는 것으로 보인다 — AABB 는 겹치니 joint-check 도 놓쳤다.
      // 하단을 통 바닥(y 0.03)에 두고 기울기 0.5 로 뽑으면 입구 높이에서 x 0.142,
      // 즉 테두리(0.15) 바로 안쪽을 지나 걸린다.
      w.part('걸레 자루');
      const handle = new THREE.CylinderGeometry(0.014, 0.014, 1.05, 10);
      // 부호가 **음수**여야 한다. rotateZ(+θ) 는 +y 를 -x 로 돌리므로 양수를 주면
      // 자루가 거꾸로 선다 — 하단이 통 밖 바닥에 닿고 상단이 통 위로 온다. 그건 걸레를
      // 담근 것이 아니라 막대를 통에 기대 놓은 모습이다. 음수면 하단이 통 바닥(0, 0.03)에
      // 들어가고 상단이 공중으로 뻗는다.
      handle.rotateZ(-0.5);
      handle.translate(0.252, 0.491, 0);
      w.add(handle, 'oak', 0.4);
      w.part('걸레 머리');
      // 통 안에 눌린 천 뭉치 — 여러 덩이로 나눠야 천으로 보인다
      for (let i = 0; i < 5; i++) {
        const a = (i / 5) * Math.PI * 2;
        box(
          w,
          'fabric',
          0.09,
          0.07,
          0.075,
          Math.cos(a) * 0.055,
          0.075,
          Math.sin(a) * 0.055,
          0.008,
          0.5,
        );
      }

      /* glb 조각이라 곁가지로 얹어 집어 드는 여닫이로 쓴다 (2026-09-16).
       * 입구 아래에 구겨 넣은 걸레 한 장 — 통 안에 손을 넣어 꺼낸다 */
      w.movable('걸레', () => {
        const rag = bevelBox(0.15, 0.045, 0.12, 0.014);
        rag.rotateY(0.6);
        rag.rotateZ(0.14);
        rag.translate(0.01, d.h * 0.78, -0.015);
        w.add(rag, 'fabric', 0.3);
      });
    },
  },

  {
    id: 'pedal_bin',
    nofront: true,
    name: '페달 쓰레기통',
    collection: 'sahol',
    category: '이야기',
    note:
      'PROPS 의 yuri_pills(「침대 옆 쓰레기통」, 2차부터)가 look:\'marker\' 라 형태가 없다 — ' +
      '그 자리를 채운다. 약통이 든 곳이므로 **안이 보여야 한다.** ' +
      '쓰레기통을 쓰레기통으로 만드는 것은 **페달과 뚜껑**이다. 그게 없으면 양동이와 ' +
      '구별되지 않는다 — 이 게임에는 양동이도 있다. 페달을 밟은 상태로 두어 뚜껑이 ' +
      '열려 있고 안이 보인다.',
    dims: {
      d: 0.24,
      h: 0.38,
      /** 벽 두께 */
      t: 0.008,
      /** 뚜껑이 열린 각도. **0 = 닫힘이 기본** */
      ajar: 0,
    },
    /* 뚜껑은 뒤 힌지(z = +r = 0.12, 높이 bodyH = 0.35)에서 뒤로 젖힌다.
     * 담는 곳은 통 속 — 버린 것을 에디터에서 직접 넣을 수 있다.
     * **부호를 틀리면 뚜껑이 통 속으로 처박힌다** (2026-09-12 열림 컷): 힌지가 뒤(+z)면 뚜껑은
     * −z 쪽으로 뻗으므로 x 회전 +θ 라야 y 가 올라간다(y' = L·sinθ). −1.5 였을 때는 뚜껑이
     * 아래로 돌아 통 안을 가로막아 「열었는데 안이 캄캄하다」가 됐다. */
    openable: [
      { part: '뚜껑', swing: 1.5, axis: 'x', pivot: [0, 0.35, 0.12], inside: { w: 0.2, h: 0.24, d: 0.2, at: [0, 0.14, 0] } },
    ],
    vary: (seed, d) => { const r = (k: number) => { const v = Math.sin(seed * 12.9898 + k * 78.233) * 43758.5453; return v - Math.floor(v); }; return { ...d, h: d.h * (0.94 + r(1) * 0.12), d: d.d * (0.96 + r(2) * 0.08) }; },
    build(w, d) {
      const r = d.d / 2;
      const bodyH = d.h - 0.03;

      // --- 통 ---
      w.part('통');
      const wall = lathe(
        [
          [r * 0.9, 0],
          [r, bodyH],
          [r - d.t, bodyH],
          [r * 0.9 - d.t, 0.01],
          [r * 0.9, 0],
        ],
        20,
      );
      w.add(wall, 'plastic', 0.45);
      w.part('바닥');
      tube(w, 'plastic', r * 0.9 - d.t, r * 0.9 - d.t, 0.012, 0, 0.006, 0, 20, 0.3);

      // --- 안에 든 것 (약통이 보여야 한다) ---
      w.part('버려진 것');
      // 구겨진 종이 몇 덩이
      for (let i = 0; i < 3; i++) {
        const a = (i / 3) * Math.PI * 2 + 0.4;
        // 바닥(y 0.018)에 얹혀야 한다. 0.1 부터 시작하면 쓰레기가 통 안 공중에 뜬다
        // 흩는 반지름이 0.05, 높이 증가폭이 0.019 면 세 덩이가 서로도 바닥에도 닿지
        // 않는다. 반지름 0.03 · 증가폭 0.012 면 아래 덩이가 바닥에 앉고 위가 그 위에 쌓인다.
        box(w, 'paper', 0.06, 0.05, 0.055, Math.cos(a) * 0.03, 0.043 + i * 0.012, Math.sin(a) * 0.03, 0.008, 0.3);
      }
      w.part('약통');
      // 흰 원통 — 이게 단서(clue_yuri_pills)의 물리 근거다
      // 약통도 쓰레기 위에 얹힌다 (tube 의 y 는 하단)
      tube(w, 'porcelain', 0.021, 0.021, 0.062, 0.035, 0.062, -0.03, 12, 0.2);
      tube(w, 'plastic', 0.023, 0.023, 0.012, 0.035, 0.124, -0.03, 12, 0.15);

      /* **뚜껑은 닫힌 채로 붙인다** (2026-09-10, Chris: 「쓰레기통 닫혀 있어야 함」).
       * 예전엔 `ajar: 0.95` 로 **열린 채** 구워져 있었다 — 기본은 0(닫힘)이다.
       * 단서가 든 통은 이 조각 대신 `pedal_bin_body`(뚜껑 없음) + `lid` 프롭 `pedal_bin_lid` 를 쓴다. */
      /* 뚜껑을 `w.movable` 로 감싼다 (2026-09-11) — 병합에서 빠져 제 그룹으로 남으므로
       * 이 조각 하나로 여닫는다. 예전엔 뚜껑 없는 몸통 + `pedal_bin_lid` 조각을 겹쳐야 했다. */
      w.movable('뚜껑', () => {
        const lidTop = new THREE.CylinderGeometry(r + 0.006, r + 0.004, 0.016, 20);
        lidTop.translate(0, 0.008, -r);
        const lidLip = bevelBox(0.07, 0.012, 0.022, 0.002);
        lidLip.translate(0, 0.02, -r * 2 + 0.02);
        for (const g of [lidTop, lidLip]) {
          g.rotateX(d.ajar);          // 0 = 닫힘
          g.translate(0, bodyH, r);
          w.add(g, 'plastic', 0.35);
        }
      });
      // 힌지 귀
      w.part('경첩');
      box(w, 'steel', 0.05, 0.014, 0.018, 0, bodyH + 0.004, r - 0.006, 0.001, 0.15);

      // --- 페달 (정체를 결정하는 부품) ---
      w.part('페달');
      const pedal = bevelBox(0.1, 0.014, 0.06, 0.002);
      pedal.rotateX(-0.14);
      // -r-0.045 는 통 하단(반지름 r*0.9)에서 27mm 벌어져 페달이 통 옆 바닥에 따로
      // 놓인 것처럼 보였다. joint-check 는 14.6mm 로 재서 「살짝 뗀 것」으로 통과시켰다.
      pedal.translate(0, 0.03, -r - 0.012);
      w.add(pedal, 'plastic', 0.2);
      w.part('페달 링크');
      // 페달에서 뚜껑까지 이어지는 막대. 이게 있어야 「밟으면 열린다」가 읽힌다
      const link = new THREE.CylinderGeometry(0.007, 0.007, bodyH * 0.92, 8);
      link.rotateZ(0.05);
      link.translate(r * 0.72, bodyH * 0.5, 0.02);
      w.add(link, 'steel', 0.2);
      w.part('발판 굽');
      box(w, 'plastic', 0.11, 0.024, 0.03, 0, 0.012, -r - 0.005, 0.002, 0.2);
    },
  },


  {
    id: 'workbench',
    name: '목공 작업대',
    collection: 'sahol',
    category: '이야기',
    note:
      '목공방의 중심이다. 문 손잡이에 묻은 톱밥이 여기서 나왔다. ' +
      '작업대를 작업대로 만드는 것은 **두꺼운 상판과 바이스**다. 얇은 상판은 책상이고, ' +
      '옆구리에 바이스가 물려 있으면 물건을 붙잡고 깎는 자리가 된다. ' +
      '상판에 도그홀(각재를 물리는 구멍) 줄을 파야 목공 작업대로 읽힌다.',
    dims: {
      w: 1.62,
      h: 0.86,
      d: 0.66,
      /** 상판 두께 — 얇으면 책상이 된다. 실물 목공 작업대는 70~90mm */
      top: 0.08,
      /** 다리 굵기 */
      leg: 0.09,
    },
    /** 바이스 움직임 턱이 고정 턱 쪽으로 조여진다 — 나사축과 T 손잡이가 같이 딸려 간다 (2026-09-16 상호작용) */
    openable: [{ part: '바이스 턱', slide: [0, 0, 0.09] }],
    build(w, d) {
      const hw = d.w / 2;
      const hd = d.d / 2;
      const topY = d.h - d.top;

      // --- 상판 ---
      w.part('상판');
      box(w, 'oak', d.w, d.top, d.d, 0, topY + d.top / 2, 0, 0.004, 1.4);
      // 도그홀 — 상판에 파인 구멍 줄. 어두운 원통을 얕게 박아 표현한다
      w.part('도그홀');
      for (let i = 0; i < 7; i++) {
        tube(w, 'ebony', 0.0135, 0.0135, 0.02, -hw + 0.18 + i * 0.2, d.h - 0.014, -hd + 0.12, 8, 0.15);
      }

      // --- 다리 넷 + 묶음대 ---
      w.part('다리');
      for (const [lx, lz] of [
        [-hw + 0.11, -hd + 0.1],
        [hw - 0.11, -hd + 0.1],
        [-hw + 0.11, hd - 0.1],
        [hw - 0.11, hd - 0.1],
      ] as const) {
        box(w, 'oak', d.leg, topY, d.leg, lx, topY / 2, lz, 0.003, 1.0);
      }
      w.part('묶음대');
      // 앞뒤로 가로지르는 대. 목공 작업대는 이게 굵어야 흔들리지 않는다
      for (const lz of [-hd + 0.1, hd - 0.1]) {
        box(w, 'oak', d.w - 0.22, 0.07, 0.05, 0, 0.22, lz, 0.003, 0.9);
      }
      box(w, 'oak', 0.05, 0.07, d.d - 0.2, -hw + 0.11, 0.22, 0, 0.003, 0.9);
      box(w, 'oak', 0.05, 0.07, d.d - 0.2, hw - 0.11, 0.22, 0, 0.003, 0.9);

      // --- 하부 선반 (자재를 쌓는 자리) ---
      w.part('하부 선반');
      box(w, 'oak', d.w - 0.24, 0.024, d.d - 0.22, 0, 0.28, 0, 0.003, 1.1);

      // --- 바이스 (정체를 결정하는 부품) ---
      // 상판 앞 왼쪽 구석에 물린다
      w.part('바이스');
      const vx = -hw + 0.24;
      // 고정 턱 (상판에 붙는 쪽)
      box(w, 'iron', 0.2, 0.11, 0.05, vx, topY - 0.05, -hd - 0.02, 0.003, 0.3);
      w.movable('바이스 턱', () => {
      // 움직이는 턱
      box(w, 'iron', 0.2, 0.11, 0.05, vx, topY - 0.05, -hd - 0.14, 0.003, 0.3);
      // 나사축
      const screw = new THREE.CylinderGeometry(0.017, 0.017, 0.22, 12);
      screw.rotateX(Math.PI / 2);
      screw.translate(vx, topY - 0.05, -hd - 0.11);
      w.add(screw, 'steel', 0.25);
      // 손잡이 봉 (T 자)
      w.part('바이스 손잡이');
      const bar = new THREE.CylinderGeometry(0.011, 0.011, 0.26, 10);
      bar.rotateZ(Math.PI / 2);
      bar.translate(vx, topY - 0.05, -hd - 0.23);
      w.add(bar, 'steel', 0.2);
      for (const sx of [-0.13, 0.13]) {
        const cap = new THREE.SphereGeometry(0.016, 8, 6);
        cap.translate(vx + sx, topY - 0.05, -hd - 0.23);
        w.add(cap, 'steel', 0.15);
      }
      });

      // --- 톱밥 (손잡이의 톱밥이 여기서 나왔다) ---
      w.part('톱밥');
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + 0.7;
        box(w, 'oak', 0.13, 0.006, 0.1, Math.cos(a) * 0.36, d.h + 0.003, Math.sin(a) * 0.16, 0.001, 0.25);
      }
    },
  },

  {
    id: 'tool_board',
    mount: 'wall',
    name: '공구 걸이판',
    collection: 'sahol',
    category: '이야기',
    note:
      '목공방 벽에 걸린 판. 벽에 붙는 것이라 **원점은 판 밑면 중앙이고 뒷면(+Z)이 벽에 닿는다.** ' +
      '걸이판을 걸이판으로 만드는 것은 **구멍 뚍린 판과 걸린 공구들의 실루엣**이다. ' +
      '빈 판은 합판이고, 공구가 걸려 있으면 「누가 정리해 쓰는 자리」가 된다. ' +
      '**빈 자리 하나가 보여야 한다** — 뭔가 하나 없어졌다는 것이 형태로 읽힌다.',
    dims: {
      w: 1.1,
      h: 0.72,
      /** 판 두께 */
      t: 0.014,
      /**
       * 구멍 간격. 0.055 로 두면 구멍이 198개가 되고 삼각형 4,752 개 — 조각 전체의 67% 를
       * 구멍이 먹는다. 이 판은 벽에 걸려 있어 손전등 거리에서 개별 구멍이 구분되지 않으므로
       * 그만큼 촘촘할 이유가 없다. 0.09 면 66개로 줄고 「구멍 뚫린 판」이라는 인상은 같다.
       */
      pitch: 0.09,
    },
    /* 갈고리에서 망치를 떼어 낸다 — 갈고리는 판에 남는다 (2026-09-16) */
    openable: [{ part: '망치', slide: [0, 0, -0.06] }],
    build(w, d) {
      const hw = d.w / 2;

      // --- 판 ---
      w.part('판');
      box(w, 'oak', d.w, d.h, d.t, 0, d.h / 2, 0, 0.003, 1.2);
      // 벽에서 띄우는 각재 넷 (페그보드는 뒤에 공간이 있어야 갈고리가 걸린다)
      w.part('띄움 각재');
      for (const sx of [-hw + 0.06, hw - 0.06]) {
        box(w, 'ebony', 0.03, d.h - 0.04, 0.02, sx, d.h / 2, d.t / 2 + 0.011, 0.002, 0.5);
      }

      // --- 구멍 ---
      // 실제로 뚫으면 삼각형이 폭발하므로 어두운 원통을 얕게 박는다 (연탄과 같은 방식)
      w.part('구멍');
      const nx = Math.floor((d.w - 0.1) / d.pitch);
      const ny = Math.floor((d.h - 0.1) / d.pitch);
      for (let i = 0; i < nx; i++) {
        for (let j = 0; j < ny; j++) {
          const hx = -((nx - 1) * d.pitch) / 2 + i * d.pitch;
          const hy = 0.06 + j * d.pitch;
          // 원통(6세그 = 24삼각형) 대신 박스(12삼각형)를 쓴다. 이 거리에서 원형과
          // 사각형은 구분되지 않고 삼각형은 절반이다.
          const hole = bevelBox(0.013, 0.013, 0.01, 0);
          hole.translate(hx, hy, -d.t / 2 + 0.002);
          w.add(hole, 'ebony', 0.12);
        }
      }

      // --- 걸린 공구 ---
      // 실루엣으로 읽혀야 한다. 자·톱·망치·드라이버 · 그리고 **빈 자리 하나**
      // 갈고리 — 잘린 호다. 부분 TorusGeometry 는 끝 단면이 뚫리므로 cappedArc 를 쓴다.
      // 갈고리가 여덟 개라 이걸 놓치면 위반이 여덟 건으로 늘어난다.
      const hookAt = (x: number, y: number) => {
        const hook = cappedArc(0.014, 0.0035, Math.PI * 1.2, 10, 5);
        hook.rotateX(Math.PI / 2);
        hook.rotateZ(Math.PI);
        hook.translate(x, y, -d.t / 2 - 0.012);
        w.add(hook, 'steel', 0.15);
      };

      w.part('톱');
      // 등대기톱 — 날은 판, 등은 두꺼운 띠.
      // x 중심을 -0.36 에 두면 날 왼쪽 끝이 -0.57 로 판 경계(-0.55)를 2cm 벗어난다.
      // 걸린 공구가 판 밖으로 나가면 「걸려 있다」가 아니라 「떠 있다」로 보인다.
      hookAt(-0.28, d.h - 0.1);
      box(w, 'steel', 0.42, 0.11, 0.004, -0.28, d.h - 0.19, -d.t / 2 - 0.02, 0.001, 0.4);
      box(w, 'steel', 0.42, 0.022, 0.012, -0.28, d.h - 0.13, -d.t / 2 - 0.02, 0.001, 0.3);
      box(w, 'oak', 0.1, 0.075, 0.024, -0.05, d.h - 0.19, -d.t / 2 - 0.02, 0.004, 0.3);

      w.part('망치');
      hookAt(0.06, d.h - 0.1);
      /* 갈고리는 판에 두고 망치만 떼어 낸다 (2026-09-16) */
      w.movable('망치', () => {
        box(w, 'oak', 0.026, 0.28, 0.026, 0.06, d.h - 0.26, -d.t / 2 - 0.024, 0.002, 0.3);
        box(w, 'iron', 0.11, 0.035, 0.035, 0.06, d.h - 0.13, -d.t / 2 - 0.024, 0.003, 0.25);
      });

      w.part('직각자');
      hookAt(0.3, d.h - 0.1);
      box(w, 'alu', 0.026, 0.3, 0.006, 0.3, d.h - 0.26, -d.t / 2 - 0.018, 0.001, 0.3);
      box(w, 'alu', 0.17, 0.026, 0.006, 0.372, d.h - 0.4, -d.t / 2 - 0.018, 0.001, 0.3);

      w.part('드라이버 셋');
      for (let i = 0; i < 4; i++) {
        const dx = -0.34 + i * 0.075;
        hookAt(dx, 0.3);
        box(w, 'plastic', 0.022, 0.09, 0.022, dx, 0.24, -d.t / 2 - 0.022, 0.002, 0.2);
        const shaft = new THREE.CylinderGeometry(0.005, 0.005, 0.1, 8);
        shaft.translate(dx, 0.145, -d.t / 2 - 0.022);
        w.add(shaft, 'steel', 0.15);
      }

      // **빈 자리** — 갈고리만 있고 공구가 없다. 하나 없어졌다는 뜻이다
      w.part('빈 자리');
      hookAt(0.24, 0.3);
    },
  },

  /* ---------------------------------------------------------------- */
  /* 3순위 — 분위기                                                    */
  /* ---------------------------------------------------------------- */
  {
    id: 'old_sofa',
    name: '낡은 소파',
    collection: 'sahol',
    category: '이야기',
    note:
      '공용 거실. 발견 뒤 다섯이 여기 서 있었다. ' +
      '「낡은」이 형태로 보여야 한다 — 새 소파는 쿠션이 팽팽하고 낡은 소파는 **앉는 자리가 ' +
      '꺼져 있다.** 그래서 좌석 쿠션 셋의 높이를 달리 하고 가운데를 가장 눌렀다. ' +
      '팔걸이가 두툼해야 소파로 읽힌다 — 얇으면 벤치다.',
    dims: {
      w: 1.85,
      h: 0.78,
      d: 0.85,
      /** 좌석 높이 */
      seat: 0.4,
      /** 팔걸이 폭 */
      arm: 0.19,
      /** 다리 높이 */
      leg: 0.09,
      /** 천 종류 0 회청 천 · 1 가죽 · 2 붉은 천 — variants.ts 프리셋이 쓴다 */
      cloth: -1,
    },
    /** 좌석 쿠션을 앞으로 들추면 몸통 속이 드러난다 — 소파 밑은 클래식한 숨김 자리 (2026-09-16 상호작용) */
    openable: [
      { part: '좌석 쿠션 1', slide: [-0.06, 0.16, -0.34], inside: { w: 1.42, h: 0.18, d: 0.55, at: [0, 0.32, -0.05] } },
      { part: '좌석 쿠션 2', slide: [0, 0.16, -0.36], inside: { w: 1.42, h: 0.18, d: 0.55, at: [0, 0.32, -0.05] } },
      { part: '좌석 쿠션 3', slide: [0.06, 0.16, -0.34], inside: { w: 1.42, h: 0.18, d: 0.55, at: [0, 0.32, -0.05] } },
    ],
    build(w, d) {
      const F = (['fabric', 'leather', 'redcloth'] as const)[d.cloth ?? -1] ?? 'fabric';   // dims.cloth 0 회청 천 · 1 가죽 · 2 붉은 천 (variants.ts)
      const hw = d.w / 2;
      const hd = d.d / 2;
      const inner = d.w - d.arm * 2;

      // --- 몸통 (좌석 밑 상자) ---
      w.part('몸통');
      box(w, F, d.w, d.seat - d.leg - 0.1, d.d, 0, d.leg + (d.seat - d.leg - 0.1) / 2, 0, 0.01, 1.2);

      // --- 등받이 ---
      // 위로 갈수록 얇아지고 살짝 뒤로 눕는다. 수직 판이면 사무용 의자가 된다
      w.part('등받이');
      const back = bevelBox(inner, d.h - d.seat + 0.1, 0.2, 0.014);
      back.rotateX(-0.09);
      back.translate(0, d.seat + (d.h - d.seat) / 2, hd - 0.11);
      w.add(back, F, 1.1);
      // 등 쿠션 둘 — 처져서 아래가 불룩하다
      w.part('등 쿠션');
      for (const sx of [-inner / 4, inner / 4]) {
        const cush = bevelBox(inner / 2 - 0.02, 0.34, 0.15, 0.03);
        cush.rotateX(-0.14);
        cush.translate(sx, d.seat + 0.2, hd - 0.24);
        w.add(cush, F, 0.9);
      }

      // --- 좌석 쿠션 셋 ---
      // **가운데를 가장 눌렀다.** 셋이 같은 높이면 새 소파로 보인다
      w.part('좌석 쿠션');
      const sagging = [0.115, 0.088, 0.108];
      for (let i = 0; i < 3; i++) {
        const cw = inner / 3 - 0.014;
        const cush = bevelBox(cw, sagging[i], d.d - 0.28, 0.026);
        cush.translate(-inner / 3 + i * (inner / 3), d.seat - 0.1 + sagging[i] / 2, -0.05);
        w.movable(`좌석 쿠션 ${i + 1}`, () => { w.add(cush, F, 0.9); });
      }

      // --- 팔걸이 ---
      // 두툼해야 소파다. 앞쪽이 살짝 낮게 굴러떨어지는 형태
      w.part('팔걸이');
      for (const sx of [-1, 1]) {
        box(w, F, d.arm, d.seat + 0.16 - d.leg, d.d, sx * (hw - d.arm / 2), d.leg + (d.seat + 0.16 - d.leg) / 2, 0, 0.02, 1.0);
        // 팔걸이 위 말이 — 굵은 원통이 굴러 있는 형태
        const roll = new THREE.CylinderGeometry(d.arm / 2, d.arm / 2, d.d, 12);
        roll.rotateX(Math.PI / 2);
        roll.translate(sx * (hw - d.arm / 2), d.seat + 0.16, 0);
        w.add(roll, F, 0.8);
      }

      // --- 다리 넷 ---
      w.part('다리');
      for (const [lx, lz] of [
        [-hw + 0.1, -hd + 0.1],
        [hw - 0.1, -hd + 0.1],
        [-hw + 0.1, hd - 0.1],
        [hw - 0.1, hd - 0.1],
      ] as const) {
        // 위가 굵고 아래가 좁은 원목 다리
        w.add(
          (() => {
            const g = lathe(
              [
                [0.026, 0],
                [0.03, 0.02],
                [0.028, d.leg],
              ],
              10,
            );
            g.translate(lx, 0, lz);
            return g;
          })(),
          'walnut',
          0.2,
        );
      }
    },
  },

  {
    id: 'darkroom_trays',
    nofront: true,
    name: '인화 트레이와 줄',
    collection: 'sahol',
    category: '이야기',
    note:
      '송이경의 집 암실. 12/18 낮에 필름을 현상했다. ' +
      '정체를 결정하는 것은 **얕은 사각 접시 셋과 줄에 매달린 인화지**다 — ' +
      '접시가 하나면 설거지통이고, 셋이 나란하면 현상·정지·정착이라는 순서가 읽힌다. ' +
      '집게에 물린 인화지가 있어야 「방금 걸었다」가 된다.',
    /** 작업대 위의 트레이와 **천장에 매달린 줄**은 따로 떠 있는 것이 맞다 */
    clusters: 2,
    dims: {
      /** 트레이 하나 */
      w: 0.3,
      d: 0.24,
      /** 트레이 깊이 (얕다 — 이게 사진 트레이의 특징) */
      h: 0.055,
      /** 작업대 높이 */
      table: 0.82,
      /** 줄 높이 */
      line: 1.62,
    },
    /* 줄에 물린 인화지 한 장을 집게에서 떼어 낸다 (2026-09-16) */
    openable: [{ part: '인화지 한 장', slide: [0, -0.03, -0.02] }],
    build(with_, d) {
      const w = with_;
      const t = 0.008;

      // --- 받침 작업대 ---
      w.part('작업대');
      box(w, 'trim', 1.1, 0.03, 0.42, 0, d.table - 0.015, 0, 0.003, 1.2);
      for (const lx of [-0.48, 0.48]) {
        for (const lz of [-0.16, 0.16]) {
          box(w, 'trim', 0.05, d.table - 0.03, 0.05, lx, (d.table - 0.03) / 2, lz, 0.003, 0.8);
        }
      }

      // --- 트레이 셋 ---
      // 사각 통이라 lathe 를 쓸 수 없다. 판 다섯 장으로 짜면 각 판이 닫힌 몸체다.
      // 열린 상자를 한 덩이로 만들면 반드시 투과된다.
      for (let i = 0; i < 3; i++) {
        const cx = -0.34 + i * 0.34;
        w.part(`트레이 ${i + 1}`);
        box(w, 'plastic', d.w, t, d.d, cx, d.table + t / 2, 0, 0.002, 0.5);
        box(w, 'plastic', d.w, d.h - t, t, cx, d.table + t + (d.h - t) / 2, -d.d / 2 + t / 2, 0.002, 0.5);
        box(w, 'plastic', d.w, d.h - t, t, cx, d.table + t + (d.h - t) / 2, d.d / 2 - t / 2, 0.002, 0.5);
        box(w, 'plastic', t, d.h - t, d.d - t * 2, cx - d.w / 2 + t / 2, d.table + t + (d.h - t) / 2, 0, 0.002, 0.5);
        box(w, 'plastic', t, d.h - t, d.d - t * 2, cx + d.w / 2 - t / 2, d.table + t + (d.h - t) / 2, 0, 0.002, 0.5);
        // 담긴 약액 — 트레이마다 높이가 다르다. 같으면 빈 접시 셋으로 보인다
        w.part(`약액 ${i + 1}`);
        const liq = [0.03, 0.022, 0.034][i];
        box(w, 'pane', d.w - t * 3, liq, d.d - t * 3, cx, d.table + t + liq / 2, 0, 0.001, 0.4);
      }

      // --- 집게 ---
      w.part('집게');
      const clip = (x: number) => {
        box(w, 'plastic', 0.014, 0.032, 0.01, x, d.line - 0.014, 0, 0.001, 0.15);
      };

      // --- 빨래줄 ---
      w.part('줄');
      const line = new THREE.CylinderGeometry(0.0022, 0.0022, 1.5, 6);
      line.rotateZ(Math.PI / 2);
      line.translate(0, d.line, 0);
      w.add(line, 'steel', 0.2);
      // **줄을 받치는 기둥 둘.** 줄만 두면 1.5m 강선이 작업대 위 공중에 떠 있다 —
      // 실제 암실은 벽에 박지만 조각 하나로 완결되려면 자립해야 한다(여섯 방향 시트에서
      // 줄과 인화지가 아무것에도 매이지 않은 채 떠 있었다). 발까지 달아 세운다.
      w.part('줄 기둥');
      for (const sx of [-0.74, 0.74]) {
        box(w, 'steel', 0.016, d.line + 0.02, 0.016, sx, (d.line + 0.02) / 2, 0, 0.002, 0.3);
        box(w, 'steel', 0.05, 0.014, 0.2, sx, 0.007, 0, 0.002, 0.2);
      }

      // --- 매달린 인화지 ---
      // paper 슬롯은 양면 재질이 아니다. 두께 없는 판으로 만들면 게이트에 걸리므로
      // 얇은 박스로 만든다 (두께 1.5mm).
      w.part('인화지');
      const sheets: [number, number, number][] = [
        [-0.5, 0.19, 0.14],
        [-0.24, 0.24, 0.18],
        [0.06, 0.17, 0.12],
        [0.34, 0.23, 0.17],
      ];
      for (const [si, [sx, sh, sw]] of sheets.entries()) {
        clip(sx);
        const drawSheet = () => {
          const sheet = bevelBox(sw, sh, 0.0015, 0.0003);
          // 살짝 비스듬히 매달린다. 나란히 정렬하면 인쇄물 견본으로 보인다
          sheet.rotateZ((sx > 0 ? 1 : -1) * 0.035);
          sheet.translate(sx, d.line - 0.03 - sh / 2, 0.001);
          w.add(sheet, 'paper', 0.3);
        };
        // 가장 큰 한 장을 여닫이로 — 집게는 줄에 남는다
        if (si === 1) w.movable('인화지 한 장', drawSheet); else drawSheet();
      }
    },
  },

  {
    id: 'work_coat_rack',
    name: '작업복 걸이',
    collection: 'sahol',
    category: '이야기',
    note:
      '명주원의 집. 최연장자이고 개인회생 3년 차인 사람의 방이다. ' +
      '옷걸이만 두면 막대이고 **어깨선이 살아 있는 옷**이 걸려 있어야 「입던 작업복」이 된다. ' +
      '어깨에서 아래로 갈수록 좁아지고 소매가 몸통에서 떨어져 나와야 옷으로 읽힌다.',
    dims: {
      /** 스탠드 높이 */
      h: 1.72,
      /** 받침 지름 */
      base: 0.44,
      /** 걸린 옷 어깨 폭 */
      shoulder: 0.46,
    },
    /* 옷걸이째 작업복 한 벌을 들어 내린다 (2026-09-16) */
    openable: [{ part: '작업복', slide: [0, 0, -0.12] }],
    build(w, d) {
      // --- 받침 + 기둥 ---
      w.part('받침');
      const foot = lathe(
        [
          [d.base / 2, 0],
          [d.base / 2, 0.014],
          [0.05, 0.05],
          [0.05, 0],
          [d.base / 2, 0],
        ],
        14,
      );
      w.add(foot, 'iron', 0.4);
      w.part('기둥');
      tube(w, 'steel', 0.019, 0.023, d.h - 0.05, 0, 0.05, 0, 12, 0.5);

      // --- 걸이 팔 ---
      // 십자로 뻗은 짧은 팔 넷 + 끝의 공 마감
      w.part('걸이 팔');
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
        const arm = new THREE.CylinderGeometry(0.011, 0.011, 0.17, 8);
        arm.rotateZ(Math.PI / 2);
        arm.rotateY(a);
        // **이동 방향은 팔이 향하는 방향과 같아야 한다.** rotateY(a) 는 x축을
        // (cos a, 0, -sin a) 로 보내는데 (sin a, 0, cos a) 로 옮기면 그 둘이 수직이라
        // 팔이 기둥에 접선으로 놓이고 아무것에도 닿지 않는다 (61~115mm 떠 있었다).
        arm.translate(Math.cos(a) * 0.085, d.h - 0.04, -Math.sin(a) * 0.085);
        w.add(arm, 'steel', 0.25);
        const knob = new THREE.SphereGeometry(0.016, 8, 6);
        knob.translate(Math.cos(a) * 0.17, d.h - 0.04, -Math.sin(a) * 0.17);
        w.add(knob, 'steel', 0.2);
      }

      // --- 옷걸이 (어깨) ---
      // 옷걸이·작업복·주머니가 한 벌로 같이 내려온다 — 걸이 팔은 스탠드에 남는다
      w.movable('작업복', () => {
      w.part('옷걸이');
      const hangerY = d.h - 0.16;
      // 어깨 두 짝이 비스듬히 — 이게 옷의 어깨선을 만든다
      for (const sx of [-1, 1]) {
        const bar = bevelBox(d.shoulder / 2, 0.014, 0.026, 0.002);
        bar.rotateZ(sx * -0.16);
        bar.translate(sx * d.shoulder / 4, hangerY, 0);
        w.add(bar, 'walnut', 0.3);
      }
      // 목 고리 — 잘린 호이므로 cappedArc (부분 TorusGeometry 는 끝이 뚫린다)
      const hook = cappedArc(0.032, 0.0045, Math.PI * 1.4, 12, 5);
      hook.rotateY(Math.PI / 2);
      hook.translate(0, hangerY + 0.045, 0);
      w.add(hook, 'steel', 0.2);

      // --- 작업복 ---
      // 어깨에서 아래로 좁아진다. 직사각 판이면 수건이다
      w.part('작업복 몸통');
      // **어깨보다 좁게 시작한다.** 옷 상단을 어깨 막대와 같은 폭으로 두면 막대가 옷에
      // 묻혀 어깨선이 사라지고 원통 자루로 보인다 (처음에 그렇게 만들었다).
      // 세그먼트도 12 로는 각져서 천으로 안 읽힌다 — 18 로 올린다.
      const topR = d.shoulder / 2 - 0.035;
      const body = lathe(
        [
          [topR, 0],
          [topR + 0.005, -0.08],
          [topR - 0.02, -0.3],
          [topR - 0.045, -0.62],
          [topR - 0.065, -0.7],
          [0, -0.7],
          [0, 0],
        ],
        18,
      );
      // lathe 는 위로 올라가는 프로파일을 기대하지만 옷은 아래로 늘어진다 —
      // ensureOutward 가 면 방향을 보정하므로 이대로 써도 된다
      body.scale(1, 1, 0.55);       // 앞뒤로 눌러 옷처럼
      body.translate(0, hangerY - 0.02, 0);
      w.add(body, 'fabric', 1.0);

      w.part('소매');
      // 몸통에서 떨어져 나온 두 짝. 이게 없으면 자루다
      for (const sx of [-1, 1]) {
        const sleeve = new THREE.CylinderGeometry(0.062, 0.052, 0.44, 10);
        sleeve.rotateZ(sx * 0.2);
        sleeve.translate(sx * (d.shoulder / 2 - 0.03), hangerY - 0.26, 0);
        w.add(sleeve, 'fabric', 0.8);
      }

      w.part('주머니');
      for (const sx of [-1, 1]) {
        box(w, 'fabric', 0.1, 0.11, 0.012, sx * 0.1, hangerY - 0.44, -0.07, 0.004, 0.4);
      }
      });
    },
  },

  {
    id: 'burlap_sack',
    name: '생두 마대 자루',
    collection: 'sahol',
    category: '이야기',
    note:
      '로스터리의 생두 자루. 정체를 결정하는 것은 **접힌 입구와 불룩한 몸통**이다 — ' +
      '수직 원통이면 드럼통이고, 아래가 퍼지고 위가 접혀 있으면 「담아서 세워 둔 자루」가 된다. ' +
      '입구가 열린 통이라 폐단면 lathe 로 만들어야 투과되지 않는다.',
    dims: {
      /** 아래 지름 (퍼진 쪽) */
      d: 0.52,
      h: 0.86,
      /** 자루 천 두께 */
      t: 0.012,
    },
    /* 자루를 통째로 끌어 옮긴다 — 생두 무게라 들지 못하고 끈다 (2026-09-16) */
    openable: [{ part: '마대 자루', slide: [0.15, 0, 0.05] }],
    build(w, d) {
      const r = d.d / 2;
      /**
       * **목을 여기서 한 번에 정한다.** 예전 값은 `r*0.72` 였는데, 그러면 몸통 최대
       * 지름 대비 0.72 라 위가 거의 안 좁아진다 — 옆선이 밑에서 70% 높이까지 수직인
       * **우유통**이 됐다(2026-09-06 시트에서 확인). note 가 「수직 원통이면 드럼통」
       * 이라고 미리 경고해 둔 바로 그 상태였다.
       *
       * **그렇다고 0.5 로 조이면 이번엔 우유병이다.** 실제로 그렇게 해 봤고 좁은
       * 축에서 병 목이 그대로 보였다. 마대는 **목이 없다** — 위를 오므리는 게
       * 아니라 접는 것이라, 접힌 입구가 몸통만큼 넓다. 0.80 이 그 값이다.
       */
      const nk = r * 0.8;

      // 자루 전체가 여닫이 — 생두까지 같이 딸려 간다
      w.movable('마대 자루', () => {
      // --- 자루 몸통 ---
      /**
       * 아래가 퍼지고 가운데가 불룩하고 위가 조인다. 폐단면으로 한 번에 돌린다.
       * **최대 지름이 아래쪽 10% 높이에 있어야 한다** — 곡물이 든 자루는 제 무게로
       * 밑이 주저앉는다. 예전에는 밑이 `r*0.86` 으로 가운데(`r*1.0`)보다 **좁아서**
       * note 가 요구한 「아래가 퍼지고」의 정반대였다.
       */
      w.part('자루');
      const sack = lathe(
        [
          [r * 0.94, 0],
          [r, d.h * 0.09],
          [r, d.h * 0.30],
          [r * 0.96, d.h * 0.52],
          [r * 0.9, d.h * 0.70],
          [r * 0.86, d.h * 0.84],
          [nk, d.h],
          [nk - d.t, d.h],
          [r * 0.86 - d.t, d.h * 0.84],
          [r * 0.9 - d.t, d.h * 0.70],
          [r * 0.96 - d.t, d.h * 0.52],
          [r - d.t, d.h * 0.30],
          [r - d.t, d.h * 0.09],
          [r * 0.94 - d.t, 0.02],
          [r * 0.94, 0],
        ],
        20,
      );
      /**
       * **회전체인 한 자루가 아니라 병이다.** 프로필을 세 번 고쳐(드럼통 → 원뿔 →
       * 배부른 통) 매번 우유통·우유병으로 읽혔다. 값을 더 만져도 같은 자리라,
       * 워크스루의 3바퀴 규칙대로 **요소를 다르게 만든다**: 자루는 제 무게로
       * 주저앉아 단면이 **타원**이 된다. 도자기·병은 원이다. 이 하나가 회전체
       * 실루엣을 깬다. 축비는 1.14 : 0.86 (면적은 거의 보존).
       */
      const OVX = 1.14, OVZ = 0.86;
      sack.scale(OVX, 1, OVZ);
      /* **`fabric` 슬롯은 청회색(#4d5262)이다** — 소파·천막이 같이 쓰는 색이라 그대로 두면
       * 마대가 회색 드럼통으로 보인다(2026-09-11 카페 컷). 삼베색으로 칠한다. 몸통은 빛을 받는
       * 면이라 조금 밝게, 바닥과 접힌 입구는 그늘이라 어둡게 — 한 색으로 칠하면 통이 된다. */
      paintGeometry(sack, '#9c8358');
      w.add(sack, 'fabric', 1.1);
      w.part('바닥');
      // 밑판도 같이 눌러야 한다 — 원판 그대로면 짧은 축에서 3cm 삐져나온다
      const base = lathe(
        [
          [0, 0.002],
          [r * 0.94 - d.t, 0.002],
          [r * 0.94 - d.t, 0.022],
          [0, 0.022],
          [0, 0.002],
        ],
        20,
      );
      base.scale(OVX, 1, OVZ);
      paintGeometry(base, '#7d6845');
      w.add(base, 'fabric', 0.5);

      // --- 접힌 입구 ---
      // 바깥으로 한 번 말아 접은 테. 이게 자루의 얼굴이다
      w.part('접힌 입구');
      const cuff = lathe(
        [
          [nk, d.h - 0.09],
          [nk * 1.14, d.h - 0.04],
          [nk * 1.14, d.h + 0.01],
          [nk * 0.97, d.h + 0.01],
          [nk * 0.97, d.h - 0.09],
          [nk, d.h - 0.09],
        ],
        20,
      );
      cuff.scale(OVX, 1, OVZ);
      paintGeometry(cuff, '#8a7450');
      w.add(cuff, 'fabric', 0.9);

      // --- 담긴 생두 (입구로 보인다) ---
      // 입도 같이 눌린다. `tube` 는 원통뿐이라 lathe 로 짜서 같은 축비로 누른다
      w.part('생두');
      const beans = lathe(
        [
          [0, d.h - 0.13],
          [nk * 0.9, d.h - 0.125],
          [nk * 0.9, d.h - 0.10],
          [0, d.h - 0.085],
          [0, d.h - 0.13],
        ],
        20,
      );
      beans.scale(OVX, 1, OVZ);
      w.add(beans, 'earthen', 0.3);

      /**
       * --- 인쇄 도장 ---
       * **평판을 곡면에 붙이면 모서리만 나온다.** 예전에는 폭 0.2 짜리 판을 반지름
       * 0.25 인 몸통에 댔다 — 활꼴 높이가 0.2²/(8·0.25) = 2cm 라 가운데는 묻히고
       * 양 끝만 튀어나와, 시트에서 **검은 사각형 두 개**로 보였다. 폭을 0.11 로
       * 줄이면 활꼴이 0.7cm 라 판 두께와 비슷해져 한 장으로 읽힌다. 그래도 살짝
       * 파고들어 윗변이 패여 보였으므로 판을 표면 **바깥**에 대고 두께를 준다.
       */
      w.part('도장');
      const stampR = r * 0.978 * OVZ;   // d.h*0.42 높이, 짧은 축(-Z) 쪽 반지름
      box(w, 'ebony', 0.11, 0.085, 0.006, 0, d.h * 0.42, -(stampR + 0.001), 0.001, 0.3);

      // --- 아래에 접힌 귀 둘 ---
      /**
       * 자루를 세우면 아래 모서리가 접혀 귀처럼 튀어나온다.
       * **몸통에 물려야 한다** — `r*0.9` 로 두었더니 joint-check 가 2덩이 16mm 로
       * 잡았다(문턱 22mm 라 통과했을 뿐 떠 있는 것은 맞다). 그렇다고 0.78 로
       * 당기면 **몸통 속으로 완전히 들어가** 귀가 사라진다(실제로 그랬다).
       * y=0.035 에서 몸통 x 반지름이 `r*0.96*OVX` = 0.2845, 귀 반폭이 0.05 이므로
       * 중심이 0.2345 를 넘어야 삐져나오고 그보다 작아야 물린다 — 0.84 가 그 사이다.
       */
      w.part('접힌 귀');
      for (const sx of [-1, 1]) {
        const ear = bevelBox(0.1, 0.055, 0.07, 0.012);
        ear.rotateY(sx * 0.5);
        ear.translate(sx * r * 0.84 * OVX, 0.035, 0);
        w.add(ear, 'fabric', 0.5);
      }
      });
    },
  },

  {
    id: 'pottery_set',
    mount: 'surface',
    nofront: true,
    name: '도자기 한 벌',
    collection: 'sahol',
    category: '이야기',
    note:
      '윤가람의 건조 선반에 올린 것들. 임세진의 물레로 만든다. ' +
      '도자기를 도자기로 만드는 것은 **굽과 아가리**다 — 아래가 살짝 좁아지는 굽이 있고 ' +
      '입이 밖으로 벌어져야 그릇으로 읽힌다. 원통이면 컵이 아니라 통이다. ' +
      '아직 굽지 않은 것(흙빛)과 구운 것(유약)이 섞여 있어야 「만드는 중」이 된다.',
    /** 사발·항아리·접시·흙덩이를 선반에 늘어놓은 한 벌이다 */
    clusters: 4,
    dims: {
      /** 사발 지름 */
      bowl: 0.19,
      /** 항아리 높이 */
      jar: 0.26,
      /** 벽 두께 */
      t: 0.007,
    },
    /* 엎어 말리는 접시 한 장을 집어 본다 (2026-09-16) */
    openable: [{ part: '접시', slide: [0, 0, -0.05] }],
    build(w, d) {
      const br = d.bowl / 2;

      // --- 사발 (구운 것 — 유약) ---
      // 위가 열린 그릇. 폐단면 lathe 로 만든다
      w.part('사발');
      const bowl = lathe(
        [
          [br * 0.42, 0],
          [br * 0.46, 0.012],
          [br * 0.86, 0.05],
          [br, 0.088],
          [br - d.t, 0.088],
          [br * 0.86 - d.t, 0.05],
          [br * 0.44 - d.t, 0.016],
          [br * 0.42, 0],
        ],
        18,
      );
      bowl.translate(-0.17, 0, 0);
      w.add(bowl, 'porcelain', 0.3);

      // --- 항아리 (아직 안 구운 것 — 흙빛) ---
      w.part('항아리');
      const jar = lathe(
        [
          [0.055, 0],
          [0.062, 0.02],
          [0.098, d.jar * 0.42],
          [0.086, d.jar * 0.76],
          [0.064, d.jar],
          [0.072, d.jar + 0.014],
          [0.072 - d.t, d.jar + 0.014],
          [0.064 - d.t, d.jar],
          [0.086 - d.t, d.jar * 0.76],
          [0.098 - d.t, d.jar * 0.42],
          [0.062 - d.t, 0.024],
          [0.055, 0],
        ],
        18,
      );
      jar.translate(0.06, 0, 0.02);
      w.add(jar, 'earthen', 0.4);

      // --- 접시 두 장 (엎어 놓은 것 — 말리는 중) ---
      w.part('접시');
      for (let i = 0; i < 2; i++) {
        const drawPlate = () => {
          const plate = lathe(
            [
              [0, 0],
              [br * 1.05, 0.012],
              [br * 1.06, 0.019],
              [0, 0.019],
            ],
            18,
          );
          plate.translate(0.25, i * 0.022, -0.13);
          w.add(plate, 'porcelain', 0.3);
        };
        // 아래 깔린 한 장을 여닫이로 — 집어 뒤집어 본다
        if (i === 0) w.movable('접시', drawPlate); else drawPlate();
      }

      // --- 굽지 않은 덩이 (물레에서 막 뗀 것) ---
      w.part('흙 덩이');
      const lump = lathe(
        [
          [0.048, 0],
          [0.056, 0.03],
          [0.05, 0.075],
          [0.036, 0.1],
          [0, 0.105],
        ],
        12,
      );
      lump.translate(-0.02, 0, -0.15);
      w.add(lump, 'earthen', 0.3);
    },
  },
  /* ---------------------------------------------------------------- */
  /* 세울 수 있는 칸막이 — 배치 에디터에서 직접 놓는다                  */
  /* ---------------------------------------------------------------- */
  {
    id: 'partition_wall',
    name: '칸막이 벽 (세울 수 있음)',
    collection: 'sahol',
    category: '건축',
    nofront: true,
    note:
      '**에디터에서 사람이 직접 세우는 벽이다** (2026-09-11, Chris: 「그 벽이나 유리벽이나 ' +
      '이런것도 내가 세울 수 있게 하면 좋지 않냐」). 레벨의 `WALL_RUNS` 는 코드로 짜야 하지만 ' +
      '이건 카탈로그 조각이라 클릭해서 놓고 끌어서 옮기고 돌리고 **축별로 늘린다**. ' +
      '기본 한 장이 1m 라 축별 크기 x 를 3 으로 주면 3m 벽이 된다 — 두께·높이는 그대로 둔다. ' +
      '원점은 밑면 가운데, 길이는 x 축. 천장(2.6)까지 닿게 높이를 잡았다.',
    dims: { len: 1.0, h: 2.6, t: 0.12 },
    build(w, d) {
      w.part('벽');
      const body = bevelBox(d.len, d.h, d.t, 0.004);
      body.translate(0, d.h / 2, 0);
      w.add(body, 'plaster', 0.9);
      // 걸레받이 — 이게 없으면 세운 판때기로 보인다
      w.part('걸레받이');
      const skirt = bevelBox(d.len, 0.1, d.t + 0.02, 0.003);
      skirt.translate(0, 0.05, 0);
      w.add(skirt, 'ebony', 0.5);
    },
  },
  {
    id: 'partition_glass',
    name: '유리 칸막이 (세울 수 있음)',
    collection: 'sahol',
    category: '건축',
    nofront: true,
    note:
      '로스팅실처럼 **보이되 막아야 하는** 경계에 쓴다 — 손님이 작업을 보되 들어오진 못한다. ' +
      '알루미늄 프레임 + 유리 한 장. 유리는 두께가 없는 면이라 `thinSurface` 로 감싼다. ' +
      '`partition_wall` 과 같은 규약: 원점 밑면 가운데, 길이 x, 기본 1m.',
    dims: { len: 1.0, h: 2.6, t: 0.06, frame: 0.05 },
    build(w, d) {
      const f = d.frame;
      w.part('프레임');
      // 위·아래 가로대
      for (const y of [f / 2, d.h - f / 2]) { const r = bevelBox(d.len, f, d.t, 0.003); r.translate(0, y, 0); w.add(r, 'steel', 0.35); }
      // 좌·우 세로대
      for (const sx of [-1, 1]) { const c = bevelBox(f, d.h - f * 2, d.t, 0.003); c.translate((sx * (d.len - f)) / 2, d.h / 2, 0); w.add(c, 'steel', 0.35); }
      w.part('유리');
      w.thinSurface(() => {
        const g = new THREE.PlaneGeometry(d.len - f * 2, d.h - f * 2);
        g.translate(0, d.h / 2, 0);
        w.add(g, 'glass', 0.05);
      });
    },
  },
  {
    id: 'file_box',
    nofront: true,
    name: '서류 보관 상자',
    collection: 'sahol',
    category: '이야기',
    note:
      'PR02. 명주원 침실 구석의 폐업 서류 상자. **뚜껑이 따로 있는 상자**라 반품 박스(플랩이 ' +
      '갈려 양옆으로)나 택배 상자(뚜껑이 옆으로 슥)와 여는 법이 다르다 — 뒤 모서리에서 **들어 올린다**.\n' +
      '**형태**: 손잡이 구멍이 양 옆에 뚫린 골판지 상자. 그 구멍이 「들고 옮긴 상자」를 만든다 — ' +
      '없으면 그냥 네모다. 뚜껑은 몸통보다 조금 커서 테두리가 걸쳐지고, 앞면에 손으로 쓴 라벨이 붙는다.\n' +
      '안에 서류철이 세워져 들어 있다. 폐업한 지 몇 해라 상자가 살짝 눌려 있어야 하지만 ' +
      '그건 배치에서 기울여 표현한다.',
    dims: { w: 0.4, h: 0.29, d: 0.32, t: 0.005, lid: 0.02 },
    /* 뚜껑은 뒤 모서리(z = +d/2)에서 젖혀 들어 올린다. 담는 곳은 상자 속 */
    openable: [
      { part: '뚜껑', swing: -2.2, axis: 'x', pivot: [0, 0.29, 0.16], inside: { w: 0.34, h: 0.2, d: 0.26, at: [0, 0.14, 0] } },
    ],
    build(w, d) {
      /* 위가 트인 몸통. 옆판에 손잡이 구멍을 내려고 판을 셋으로 쪼갠다 —
       * 통판에 오목판을 붙이면 앞에서 구멍으로 안 보인다(서류함 앞판에서 겪은 것) */
      w.part('상자');
      const oW = 0.1, oH = 0.03, oy = d.h * 0.7;
      for (const sx of [-1, 1] as const) {
        const px = sx * (d.w / 2 - d.t / 2);
        const parts: [number, number, number][] = [
          [(oy + oH / 2 + d.h) / 2, d.h - oy - oH / 2, 0],
          [(oy - oH / 2) / 2, oy - oH / 2, 0],
          [oy, oH, -(oW + (d.d - oW) / 2) / 2],
          [oy, oH, (oW + (d.d - oW) / 2) / 2],
        ];
        for (const [py, ph, pz] of parts) {
          const pd = ph === oH ? (d.d - oW) / 2 : d.d - d.t * 2;
          const g = bevelBox(d.t, ph, pd, 0.002);
          g.translate(px, py, pz);
          w.add(g, 'cardboard', 0.4);
        }
      }
      for (const sz of [-1, 1] as const) {
        const g = bevelBox(d.w, d.h, d.t, 0.002);
        g.translate(0, d.h / 2, sz * (d.d / 2 - d.t / 2));
        w.add(g, 'cardboard', 0.4);
      }
      const bot = bevelBox(d.w, d.t, d.d, 0.002);
      bot.translate(0, d.t / 2, 0);
      w.add(bot, 'cardboard', 0.4);

      w.part('라벨');
      w.thinSurface(() => {
        const lb = new THREE.PlaneGeometry(d.w * 0.5, d.h * 0.3);
        lb.rotateY(Math.PI);
        lb.translate(0, d.h * 0.45, -d.d / 2 - 0.0012);
        // **UV 타일을 0 으로 주지 마라** — localUV 가 0 으로 나눠 NaN UV 가 되고, 그 조각이
        // 들어간 시트의 front 컷이 통째로 검게 나왔다(2026-09-11, 두 번 재현). 실치수 타일을 준다
        w.add(lb, 'paper', 0.4);
      });

      w.part('서류철');
      /* 세워서 꽂는다 — 눕히면 상자가 아니라 짐칸이 된다. 뒤로 갈수록 기운다 */
      for (let i = 0; i < 6; i++) {
        const fh = 0.2 + rnd(i * 3.1) * 0.03;
        const fl = bevelBox(d.w - 0.05, fh, 0.005, 0.0012);
        fl.rotateX(-0.04 - rnd(i * 5.3) * 0.05);
        fl.translate(0, d.t + fh / 2, -d.d * 0.28 + i * 0.045);
        w.add(fl, 'paper', 0.25);
      }

      /* 뚜껑 — 몸통보다 조금 커서 테두리가 걸쳐진다. 치마(아래로 내린 띠)가 있어야 얹힌 것으로 보인다 */
      w.movable('뚜껑', () => {
        const top = bevelBox(d.w + 0.012, d.lid, d.d + 0.012, 0.003);
        top.translate(0, d.h + d.lid / 2, 0);
        w.add(top, 'cardboard', 0.4);
        for (const sx of [-1, 1] as const) {
          const g = bevelBox(d.t, 0.035, d.d + 0.012, 0.002);
          g.translate(sx * (d.w / 2 + 0.006 - d.t / 2), d.h - 0.0175 + d.lid, 0);
          w.add(g, 'cardboard', 0.4);
        }
        for (const sz of [-1, 1] as const) {
          const g = bevelBox(d.w + 0.012, 0.035, d.t, 0.002);
          g.translate(0, d.h - 0.0175 + d.lid, sz * (d.d / 2 + 0.006 - d.t / 2));
          w.add(g, 'cardboard', 0.4);
        }
      });
    },
  },
  {
    id: 'bedding',
    openable: [
      { part: '이불', node: 'quilt', slide: [0, 0.01, -0.35] },   // 발치로 끌어내린다 — 접으면(회전) 딱딱한 모델이 베개 위에 널판처럼 얹힌다(2026-09-15 Chris). 머리맡 밑이 드러난다
      { part: '베개', node: 'pillow', slide: [0, 0.14, -0.28] },   // 베개를 들춰 밑을 본다 — 베개 밑은 고전적인 숨김 자리
    ],
    nofront: true,
    name: '구겨진 이불과 베개',
    collection: 'sahol',
    category: '이야기',
    note:
      '침대 위에 걷어찬 채 둔 솜이불과 베개. **블렌더로 구운 GLB** (tools/blender/bedding.py) — 천의 ' +
      '주름은 코드 조각(박스·회전체)으로는 안 나온다. 한쪽 귀퉁이가 젖혀 올라가 「일어난 지 얼마 안 된 ' +
      '침대」를 만든다. 아래 build 는 GLB 가 못 올 때의 대체이자 **크기 계약**(fit box)이다 — ' +
      'attachGlb 가 이 상자 높이에 모델을 맞춘다. 이불은 매트리스 위 22cm 안에 눕는다.',
    glb: '/models/sahol/bedding',
    dims: { w: 0.9, d: 1.5, h: 0.24 },   // 계약 상자는 작게 — 솔버의 상판 지지 검사(90%)를 넘기려고. GLB 는 spill 1.3 안에서 실제 크기(0.98×1.78)로 선다
    clusters: 2,
    build(w, d) {
      w.part('이불');
      const q = bevelBox(d.w * 0.98, 0.07, d.d * 0.92, 0.02);
      q.translate(0, 0.035, 0.05);
      w.add(q, 'fabric', 0.6);
      w.part('베개');
      const pl = bevelBox(0.52, 0.11, 0.38, 0.03);
      pl.translate(-0.08, 0.055 + 0.07, -d.d * 0.39);
      w.add(pl, 'fabric', 0.5);
    },
  },
  /* ── 블렌더 GLB 소품 (2026-09-14, tools/blender/props.py). build 는 대체이자 크기 계약 ── */
  { id: 'cushion', openable: [{ part: '쿠션', node: '*', slide: [0, 0.35, 0] }], nofront: true, name: '쿠션', collection: 'sahol', category: '이야기', glb: '/models/sahol/cushion',
    note: '소파·의자 위 방석. 구겨진 천이라 GLB.', dims: { w: 0.44, d: 0.44, h: 0.12 },
    build(w, d) { w.part('쿠션'); const g = bevelBox(d.w, d.h, d.d, 0.03, 2); g.translate(0, d.h / 2, 0); w.add(g, 'fabric', 0.5); } },
  { id: 'curtain', nofront: true, mount: 'wall', name: '커튼', collection: 'sahol', category: '이야기', glb: '/models/sahol/curtain',
    openable: [{ part: '커튼', node: 'Plane', slide: [-0.5, 0, 0] }],   // 천 폭이 봉 왼쪽 끝으로 걷힌다 — 창을 가린 커튼을 젖히는 동작
    note: '봉에 건 커튼 한 폭. 주름은 GLB. 창 옆 벽에 건다 — 원점은 바닥, 봉 높이 2.0.', dims: { w: 1.5, d: 0.12, h: 2.05 },
    build(w, d) { w.part('커튼'); const g = bevelBox(d.w, d.h - 0.05, 0.03, 0.01); g.translate(0, (d.h - 0.05) / 2, 0); w.add(g, 'redcloth', 0.5); w.part('봉'); const r = new THREE.CylinderGeometry(0.012, 0.012, d.w + 0.2, 8); r.rotateZ(Math.PI / 2); r.translate(0, d.h - 0.03, 0.02); w.add(r, 'walnut', 0.3); } },
  { id: 'food_tray', nofront: true, name: '밥상', collection: 'sahol', category: '이야기', glb: '/models/sahol/food_tray',
    note: '쟁반에 밥·국·반찬 둘·수저. 그릇의 곡면과 음식이 GLB.', dims: { w: 0.5, d: 0.36, h: 0.09 },
    /* 밥상을 통째로 들어 옮긴다 (2026-09-16) */
    openable: [{ part: '밥상', node: '*', slide: [0, 0, -0.06] }],
    build(w, d) { w.part('쟁반'); const g = bevelBox(d.w, 0.024, d.d, 0.008); g.translate(0, 0.012, 0); w.add(g, 'walnut', 0.5); w.part('그릇'); for (const [x, z, r] of [[-0.15, -0.06, 0.06], [-0.02, -0.06, 0.07], [0.14, -0.08, 0.05], [0.14, 0.08, 0.05]] as const) { const b = new THREE.CylinderGeometry(r, r * 0.6, 0.055, 14); b.translate(x, 0.024 + 0.028, z); w.add(b, 'porcelain', 0.2); } } },
  { id: 'briquette_ash', nofront: true, name: '연탄재', collection: 'sahol', category: '이야기', glb: '/models/sahol/briquette_ash',
    note: '다 탄 연탄. 회백색에 귀퉁이가 부서져 있다. 새 연탄(briquette) 옆에 두면 「쓰는 난로」가 된다.', dims: { d: 0.15, h: 0.142 }, clusters: 2,
    /* 연탄재를 집어 살핀다 — 부서진 귀퉁이가 단서 (2026-09-16) */
    openable: [{ part: '연탄재', node: '*', slide: [0, 0, -0.06] }],
    build(w, d) { w.part('연탄재'); const g = new THREE.CylinderGeometry(d.d / 2, d.d / 2, d.h, 18); g.translate(0, d.h / 2, 0); w.add(g, 'plaster', 0.2); } },
];
